import React, { useState, useEffect } from 'react';
import { usePermissions } from '../../../utils/permissions';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  Container,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Paper,
  Divider,
  Stack,
  useTheme,
  alpha,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  Avatar,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Switch,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Science as ScienceIcon,
  LocalDrink as LocalDrinkIcon,
  Factory as FactoryIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  Scale as ScaleIcon,
  Inventory as InventoryIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Timeline as TimelineIcon,
  ExpandMore as ExpandMoreIcon,
  AttachMoney as AttachMoneyIcon,
  Speed as SpeedIcon,
  Opacity as OpacityIcon,
  Calculate as CalculateIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  AccessTime as AccessTimeIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { supabase } from '../../../supabaseClient';
import { convertUnits, getUnitType } from '../../../utils/unitConversions';

// Normalize unit variations to standard forms for cosmetics industry
const normalizeProductVolumeUnit = (unit) => {
  const unitMap = {
    // Weight units (oz = weight ounces)
    'ounces': 'oz',
    'oz': 'oz',
    'pounds': 'lbs',
    'pound': 'lbs',
    'grams': 'g',
    'gram': 'g',
    'kilograms': 'kg',
    'kilogram': 'kg',
    
    // Volume units
    'gallons': 'gal',
    'gallon': 'gal',
    'liters': 'L',
    'liter': 'L',
    'l': 'L', // lowercase L
    'milliliters': 'ml',
    'milliliter': 'ml'
  };
  return unitMap[unit] || unit;
};

// Date utility functions to avoid timezone issues
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  try {
    // If it's already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Parse as local date to avoid timezone shifts
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (e) {
    return '';
  }
};

const formatDateForDisplay = (dateString) => {
  if (!dateString) return 'Not set';
  try {
    // Parse as local date to avoid timezone shifts
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return dateString;
  }
};

// Parse date string as local date for comparisons
const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  try {
    const [year, month, day] = dateString.split('-');
    return new Date(year, month - 1, day);
  } catch (e) {
    return null;
  }
};

const BatchOverview = ({
  batch,
  formulations,
  products,
  showSnackbar,
  isCreatingNew,
  userCompanyId,
  onUpdate,
  onBackToList,
  user
}) => {
  const theme = useTheme();
  const { canAccessPage, canEditPage, canCreateProductionBatches } = usePermissions();
  
  const [batchTimeFormat, setBatchTimeFormat] = useState('24hour');
  const [userProfile, setUserProfile] = useState(null);
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [validationError, setValidationError] = useState('');
  const [productVariants, setProductVariants] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedFormulation, setSelectedFormulation] = useState(null);
  const [variantInputValues, setVariantInputValues] = useState({});
  const [rawMaterialsInventory, setRawMaterialsInventory] = useState([]);
  const [rawMaterialsInventoryItems, setRawMaterialsInventoryItems] = useState([]);
  const [formulationIngredients, setFormulationIngredients] = useState([]);
  const [batchAnalysis, setBatchAnalysis] = useState(null);
  const [calendarBatches, setCalendarBatches] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Unit Configuration states
  const [unitConfigurations, setUnitConfigurations] = useState([]);
  const [selectedUnitConfig, setSelectedUnitConfig] = useState(null);
  const [unitInputs, setUnitInputs] = useState({});  // Now stores per-variant inputs
  
  // Unit Configuration Creation states
  const [unitConfigDialogOpen, setUnitConfigDialogOpen] = useState(false);
  const [editingUnitConfig, setEditingUnitConfig] = useState(null);
  const [unitConfigFormData, setUnitConfigFormData] = useState({
    name: '',
    base_unit_name: 'units',
    enable_cases: true,
    case_unit_name: 'cases',
    units_per_case: 12,
    enable_pallets: true,
    pallet_unit_name: 'pallets',
    cases_per_pallet: 42
  });
  const [savingUnitConfig, setSavingUnitConfig] = useState(false);
  
  // Location details data
  const [locationCapacities, setLocationCapacities] = useState([]);
  
  // Pagination states for location details
  const [capacitiesPage, setCapacitiesPage] = useState(0);
  const [capacitiesRowsPerPage, setCapacitiesRowsPerPage] = useState(5);

  // Dialog states for product, formulation, and location selection
  const [productSelectionDialogOpen, setProductSelectionDialogOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [dialogSelectedProduct, setDialogSelectedProduct] = useState(null);
  const [formulationSelectionDialogOpen, setFormulationSelectionDialogOpen] = useState(false);
  const [formulationSearchTerm, setFormulationSearchTerm] = useState('');
  const [locationSelectionDialogOpen, setLocationSelectionDialogOpen] = useState(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const [unitConfigSelectionDialogOpen, setUnitConfigSelectionDialogOpen] = useState(false);
  const [unitConfigSearchTerm, setUnitConfigSearchTerm] = useState('');

  // Accordion state management with localStorage persistence
  const [accordionStates, setAccordionStates] = useState({
    additionalIdentifiers: false,
    productFormulation: true, // This was defaultExpanded originally
    locationSchedule: false,
    densityConversionDetails: false,
    ingredientAvailability: false,
    productionDetails: false,
    productionNotes: false,
    timeline: false,
    unitBreakdown: false
  });

  // State for expanding "show more" sections
  const [showAllIngredients, setShowAllIngredients] = useState(false);
  const [showAllShortages, setShowAllShortages] = useState(false);
  const [showAllExpiringMaterials, setShowAllExpiringMaterials] = useState(false);
  const [showAllEditDialogIngredients, setShowAllEditDialogIngredients] = useState(false);
  const [showAllEditDialogExpiringMaterials, setShowAllEditDialogExpiringMaterials] = useState(false);

  // Load accordion states from localStorage on mount
  useEffect(() => {
    const savedStates = localStorage.getItem(`batchOverview_accordions_${batch?.id || 'default'}`);
    if (savedStates) {
      try {
        setAccordionStates(JSON.parse(savedStates));
      } catch (error) {
        console.warn('Failed to parse saved accordion states:', error);
      }
    }
  }, [batch?.id]);

  // Save accordion states to localStorage when they change
  const updateAccordionState = (accordionKey, expanded) => {
    const newStates = { ...accordionStates, [accordionKey]: expanded };
    setAccordionStates(newStates);
    localStorage.setItem(`batchOverview_accordions_${batch?.id || 'default'}`, JSON.stringify(newStates));
  };

  const fetchLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('company_id', userCompanyId)
        .eq('is_active', true)
        .order('is_main', { ascending: false })
        .order('name');

      if (error) throw error;
      
      // Sort so main location is first, then alphabetically
      const sortedLocations = (data || []).sort((a, b) => {
        if (a.is_main && !b.is_main) return -1;
        if (!a.is_main && b.is_main) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setLocations(sortedLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([]);
    }
  };

  const fetchUnitConfigurations = async () => {
    try {
      const { data, error } = await supabase
        .from('unit_configurations')
        .select('*')
        .eq('company_id', userCompanyId)
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      
      // Sort by total units per pallet (descending), then by name
      const sortedConfigs = (data || []).sort((a, b) => {
        const getTotalUnitsPerPallet = (config) => {
          if (config.enable_pallets && config.enable_cases) {
            return config.units_per_case * config.cases_per_pallet;
          } else if (config.enable_cases) {
            return config.units_per_case;
          } else {
            return 1;
          }
        };
        
        const aTotal = getTotalUnitsPerPallet(a);
        const bTotal = getTotalUnitsPerPallet(b);
        
        if (bTotal !== aTotal) {
          return bTotal - aTotal;
        }
        return a.name.localeCompare(b.name);
      });
      
      setUnitConfigurations(sortedConfigs);
    } catch (err) {
      console.error('Error fetching unit configurations:', err);
    }
  };

  const fetchCalendarBatches = async () => {
    try {
      const { data, error } = await supabase
        .from('batch_calendar')
        .select('*')
        .eq('company_id', userCompanyId)
        .order('planned_start_date', { ascending: true });
      
      if (error) throw error;
      setCalendarBatches(data || []);
    } catch (error) {
      console.error('Error fetching calendar batches:', error);
      setCalendarBatches([]);
    }
  };

  const fetchRawMaterialsInventory = async (locationId) => {
    if (!locationId) {
      setRawMaterialsInventory([]);
      setRawMaterialsInventoryItems([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('raw_materials_inventory')
        .select(`
          *,
          raw_material:raw_material_id(
            id,
            name,
            sku,
            primary_unit_type,
            primary_unit_quantity,
            primary_unit_measure,
            category,
            material_type,
            density_g_ml
          ),
          location:location_id(id, name)
        `)
        .eq('company_id', userCompanyId)
        .eq('location_id', locationId)
        .gt('current_unit_quantity', 0)
        .order('current_unit_quantity', { ascending: false });

      if (error) throw error;
      setRawMaterialsInventory(data || []);

      // Also fetch inventory items with expiry dates
      const { data: itemsData, error: itemsError } = await supabase
        .from('raw_materials_inventory_items')
        .select(`
          *,
          raw_material:raw_material_id(
            id,
            name,
            sku,
            primary_unit_type,
            primary_unit_quantity,
            primary_unit_measure,
            category,
            material_type,
            density_g_ml
          )
        `)
        .eq('company_id', userCompanyId)
        .eq('location_id', locationId)
        .eq('is_active', true)
        .gt('current_unit_quantity', 0)
        .order('expiry_date', { ascending: true });

      if (itemsError) throw itemsError;
      setRawMaterialsInventoryItems(itemsData || []);
    } catch (error) {
      console.error('Error fetching raw materials inventory:', error);
      setRawMaterialsInventory([]);
      setRawMaterialsInventoryItems([]);
    }
  };

  const fetchLocationDetails = async (locationId) => {
    if (!locationId) {
      setLocationCapacities([]);
      return;
    }

    try {
      // Load product capacities
      try {
        const { data: capacities, error: capacitiesError } = await supabase
          .from('location_product_capacities')
          .select(`
            *,
            product:product_id(
              name,
              sku
            )
          `)
          .eq('company_id', userCompanyId)
          .eq('location_id', locationId);

        if (capacitiesError && capacitiesError.code !== 'PGRST204') {
          console.error('Error loading capacities:', capacitiesError);
        } else {
          setLocationCapacities(capacities || []);
        }
      } catch (err) {
        console.error('Capacities table may not exist:', err);
      }

    } catch (err) {
      console.error('Error loading location details:', err);
    }
  };

  const fetchFormulationIngredients = async (formulationId) => {
    if (!formulationId) {
      setFormulationIngredients([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('formulation_ingredients_new')
        .select(`
          *,
          raw_materials:ingredient_id (
            id, name, cas_number, sku, category, 
            material_type, primary_unit_measure, color, form,
            density_g_ml, primary_unit_type, primary_unit_quantity
          )
        `)
        .eq('formulation_id', formulationId)
        .order('sequence');

      if (error) throw error;
      setFormulationIngredients(data || []);
    } catch (error) {
      console.error('Error loading formulation ingredients:', error);
      setFormulationIngredients([]);
    }
  };

  const fetchProductVariants = async (productId) => {
    try {
      // Fetch from product_variants table
      const { data: tableVariants, error: tableError } = await supabase
        .from('product_variants')
        .select('*')
        .eq('product_id', productId)
        .eq('is_active', true);

      if (tableError) throw tableError;

      // Also fetch the product to check for JSONB variants
      const { data: productData, error: productError } = await supabase
        .from('products')
        .select('variants, has_variants')
        .eq('id', productId)
        .single();

      if (productError) throw productError;

      let allVariants = [];

      // Add variants from product_variants table
      if (tableVariants && tableVariants.length > 0) {
        allVariants = tableVariants.map(variant => ({
          id: variant.id,
          name: variant.name,
          volume_weight: variant.volume_weight,
          volume_unit: variant.volume_unit || 'oz',
          price: variant.price,
          sku: variant.sku,
          source: 'table'
        }));
      }

      // Add variants from JSONB column if they exist
      if (productData?.variants && Array.isArray(productData.variants)) {
        const jsonbVariants = productData.variants.map(variant => ({
          id: variant.id || variant.name,
          name: variant.name,
          volume_weight: variant.volume_weight,
          volume_unit: variant.volume_unit || 'oz',
          price: variant.price,
          sku: variant.sku,
          source: 'jsonb'
        }));
        allVariants = [...allVariants, ...jsonbVariants];
      }

      // Sort variants by volume_weight descending (largest first: 16oz, 14oz, 12oz, etc.)
      const sortedVariants = allVariants.sort((a, b) => {
        const volumeA = parseFloat(a.volume_weight) || 0;
        const volumeB = parseFloat(b.volume_weight) || 0;
        return volumeB - volumeA; // Descending order (largest first)
      });

      setProductVariants(sortedVariants);
      return sortedVariants;
    } catch (error) {
      console.error('Error fetching product variants:', error);
      setProductVariants([]);
      return [];
    }
  };

  const calculateBatchAnalysis = (isEditMode = false) => {
    const currentData = (isEditMode || editDialogOpen) ? editData : batch;
    
    if (!formulationIngredients.length || !currentData?.planned_quantity || !selectedFormulation) {
      setBatchAnalysis(null);
      return;
    }
    
    // Handle case where there are no raw materials in inventory at all
    if (!rawMaterialsInventory.length) {
      const analysis = {
        canMakeBatch: false,
        maxBatches: 0,
        shortages: [],
        ingredients: [],
        noInventoryAtLocation: true
      };
      
      // Calculate what would be needed using proper density conversion
      try {
        const formulation = selectedFormulation;
        const formulationBatchSize = formulation.target_batch_size || 100;
        const formulationUnit = formulation.unit_of_measure || 'gal';
        const targetBatchSize = currentData.planned_quantity;
        const targetUnit = currentData.planned_unit_of_measure || 'oz';

        // Calculate weighted average density from formulation ingredients
        let calculatedDensity = 1.0;
        
        if (formulationIngredients.length > 0) {
          let totalWeightedDensity = 0;
          let totalPercentage = 0;
          
          formulationIngredients.forEach(ingredient => {
            const rawMaterial = ingredient.raw_materials;
            const percentage = ingredient.percentage || 0;
            const materialDensity = rawMaterial?.density_g_ml;
            
            if (materialDensity && materialDensity > 0 && percentage > 0) {
              totalWeightedDensity += (materialDensity * percentage);
              totalPercentage += percentage;
            }
          });
          
          if (totalPercentage > 0) {
            calculatedDensity = totalWeightedDensity / totalPercentage;
          }
        }

        // Calculate scaling factor using same logic as main analysis
        let scalingFactor = 1;
        
        try {
          if (targetUnit !== formulationUnit) {
            const targetUnitType = getUnitType(targetUnit);
            const formulationUnitType = getUnitType(formulationUnit);
            
            if (targetUnitType !== formulationUnitType) {
              const density = calculatedDensity;
              
              if (formulationUnitType === 'volume' && targetUnitType === 'weight') {
                // Convert formulation volume to weight using unitConversions.js with density
                const formulationInTargetUnits = convertUnits(formulationBatchSize, formulationUnit, targetUnit, density);
                scalingFactor = targetBatchSize / formulationInTargetUnits;
              } else if (formulationUnitType === 'weight' && targetUnitType === 'volume') {
                // Convert formulation weight to volume using unitConversions.js with density
                const formulationInTargetUnits = convertUnits(formulationBatchSize, formulationUnit, targetUnit, density);
                scalingFactor = targetBatchSize / formulationInTargetUnits;
              }
            } else {
              // Same unit types - direct conversion
              const targetInFormulationUnits = convertUnits(targetBatchSize, targetUnit, formulationUnit);
              scalingFactor = targetInFormulationUnits / formulationBatchSize;
            }
          } else {
            scalingFactor = targetBatchSize / formulationBatchSize;
          }
        } catch (error) {
          console.warn('Unit conversion failed, using density-based fallback:', error);
          const density = calculatedDensity;
          const formulationInTargetUnits = convertUnits(formulationBatchSize, formulationUnit, targetUnit, density);
          scalingFactor = targetBatchSize / formulationInTargetUnits;
        }

        formulationIngredients.forEach(ingredient => {
          const rawMaterial = ingredient.raw_materials;
          if (!rawMaterial) return;
          
          // Calculate required amount in raw material units
          const percentageDecimal = (ingredient.percentage || 0) / 100;
          const baseAmountInFormulationUnits = formulationBatchSize * percentageDecimal;
          const scaledAmountInFormulationUnits = baseAmountInFormulationUnits * scalingFactor;
          
          const rawMaterialUnit = rawMaterial.primary_unit_measure || 'g';
          let requiredInRawMaterialUnits = scaledAmountInFormulationUnits;
          
          // Convert from formulation units to raw material units
          try {
            if (formulationUnit !== rawMaterialUnit) {
              const formulationUnitType = getUnitType(formulationUnit);
              const rawMaterialUnitType = getUnitType(rawMaterialUnit);
              
              if (formulationUnitType !== rawMaterialUnitType) {
                // Different unit types - use material density
                const materialDensity = rawMaterial.density_g_ml || 1.0;
                requiredInRawMaterialUnits = convertUnits(
                  scaledAmountInFormulationUnits,
                  formulationUnit,
                  rawMaterialUnit,
                  materialDensity
                );
              } else {
                // Same unit types - direct conversion
                requiredInRawMaterialUnits = convertUnits(
                  scaledAmountInFormulationUnits,
                  formulationUnit,
                  rawMaterialUnit
                );
              }
            }
          } catch (error) {
            console.warn(`Unit conversion failed for ${rawMaterial.name}:`, error);
            requiredInRawMaterialUnits = scaledAmountInFormulationUnits;
          }
          
          analysis.shortages.push({
            name: rawMaterial.name,
            required: requiredInRawMaterialUnits,
            available: 0,
            shortage: requiredInRawMaterialUnits,
            unit: rawMaterialUnit,
            noInventory: true
          });
          
          analysis.ingredients.push({
            name: rawMaterial.name,
            required: requiredInRawMaterialUnits,
            requiredUnit: rawMaterialUnit,
            available: 0,
            availableUnit: rawMaterialUnit,
            canMake: 0,
            hasInventory: false
          });
        });
      } catch (error) {
        console.error('Error in no-inventory analysis:', error);
      }
      
      setBatchAnalysis(analysis);
      return;
    }

    try {
      const formulation = selectedFormulation;
      const formulationBatchSize = formulation.target_batch_size || 100;
      const formulationUnit = formulation.unit_of_measure || 'gal';
      const targetBatchSize = currentData.planned_quantity;
      const targetUnit = currentData.planned_unit_of_measure || 'oz';

      // Calculate weighted average density from formulation ingredients (SAME AS MAIN SCALING)
      let calculatedDensity = null;
      
      if (formulationIngredients.length > 0) {
        let totalWeightedDensity = 0;
        let totalPercentage = 0;
        
        formulationIngredients.forEach(ingredient => {
          const rawMaterial = ingredient.raw_materials;
          const percentage = ingredient.percentage || 0;
          const materialDensity = rawMaterial?.density_g_ml;
          
          if (materialDensity && materialDensity > 0 && percentage > 0) {
            totalWeightedDensity += (materialDensity * percentage);
            totalPercentage += percentage;
          }
        });
        
        if (totalPercentage > 0) {
          calculatedDensity = totalWeightedDensity / totalPercentage;
        }
      }

      // Calculate scaling factor - USE SAME LOGIC AS MAIN SCALING FUNCTION
      let scalingFactor = 1;
      let targetInFormulationUnits = targetBatchSize;
      
      try {
        if (targetUnit !== formulationUnit) {
          // Check if we're trying to convert between volume and weight
          const targetUnitType = getUnitType(targetUnit);
          const formulationUnitType = getUnitType(formulationUnit);
          
          if (targetUnitType !== formulationUnitType) {
            // Different unit types (volume vs weight) - use calculated density
            const density = calculatedDensity || 1.0;
            
            if (formulationUnitType === 'volume' && targetUnitType === 'weight') {
              // Convert formulation volume to weight using unitConversions.js with density
              const formulationInTargetUnits = convertUnits(formulationBatchSize, formulationUnit, targetUnit, density);
              scalingFactor = targetBatchSize / formulationInTargetUnits;
            } else if (formulationUnitType === 'weight' && targetUnitType === 'volume') {
              // Convert formulation weight to volume using unitConversions.js with density
              const formulationInTargetUnits = convertUnits(formulationBatchSize, formulationUnit, targetUnit, density);
              scalingFactor = targetBatchSize / formulationInTargetUnits;
            }
          } else {
            // Same unit types - safe to convert
            targetInFormulationUnits = convertUnits(targetBatchSize, targetUnit, formulationUnit);
            scalingFactor = targetInFormulationUnits / formulationBatchSize;
          }
        } else {
          scalingFactor = targetBatchSize / formulationBatchSize;
        }
      
      } catch (error) {
        console.warn('Unit conversion failed for batch analysis, using density-based fallback:', error);
        // Use density-based scaling with unitConversions.js
        const density = calculatedDensity || 1.0;
        const formulationInTargetUnits = convertUnits(formulationBatchSize, formulationUnit, targetUnit, density);
        scalingFactor = targetBatchSize / formulationInTargetUnits;
      }

      const analysis = {
        canMakeBatch: true,
        maxBatches: Infinity,
        shortages: [],
        ingredients: []
      };

      formulationIngredients.forEach(ingredient => {
        const rawMaterial = ingredient.raw_materials;
        if (!rawMaterial) return;

        // Find inventory for this raw material
        const inventory = rawMaterialsInventory.find(inv => 
          inv.raw_material_id === rawMaterial.id
        );

        // Calculate required amount for one target batch in raw material units
        const percentageDecimal = (ingredient.percentage || 0) / 100;
        const baseAmount = formulationBatchSize * percentageDecimal;
        const scaledAmount = baseAmount * scalingFactor;
        
        // Convert required amount from formulation units to raw material units
        let requiredInRawMaterialUnits = scaledAmount;
        const rawMaterialUnit = rawMaterial.primary_unit_measure || 'g';
        
        try {
          if (formulationUnit !== rawMaterialUnit) {
            const formulationUnitType = getUnitType(formulationUnit);
            const rawMaterialUnitType = getUnitType(rawMaterialUnit);
            
            if (formulationUnitType !== rawMaterialUnitType) {
              // Different unit types - need density
              const materialDensity = rawMaterial.density_g_ml || 1.0;
              requiredInRawMaterialUnits = convertUnits(
                scaledAmount,
                formulationUnit,
                rawMaterialUnit,
                materialDensity
              );
            } else {
              // Same unit types - direct conversion
              requiredInRawMaterialUnits = convertUnits(
                scaledAmount,
                formulationUnit,
                rawMaterialUnit
              );
            }
          }
        } catch (error) {
          console.warn(`Unit conversion failed for ${rawMaterial.name}:`, error);
          requiredInRawMaterialUnits = scaledAmount;
        }

        const ingredientAnalysis = {
          name: rawMaterial.name,
          required: requiredInRawMaterialUnits,
          requiredUnit: rawMaterialUnit,
          available: 0,
          availableUnit: rawMaterialUnit,
          canMake: 0,
          hasInventory: !!inventory
        };

        if (inventory) {
          // Use available inventory directly (already in raw material units)
          const availableInRawMaterialUnits = inventory.current_total_quantity || 0;

          ingredientAnalysis.available = availableInRawMaterialUnits;
          ingredientAnalysis.canMake = requiredInRawMaterialUnits > 0 ? Math.floor(availableInRawMaterialUnits / requiredInRawMaterialUnits) : Infinity;

          // Update max batches based on this ingredient
          if (ingredientAnalysis.canMake < analysis.maxBatches) {
            analysis.maxBatches = ingredientAnalysis.canMake;
          }

          // Check if this ingredient is a shortage
          if (availableInRawMaterialUnits < requiredInRawMaterialUnits) {
            analysis.shortages.push({
              name: rawMaterial.name,
              required: requiredInRawMaterialUnits,
              available: availableInRawMaterialUnits,
              shortage: requiredInRawMaterialUnits - availableInRawMaterialUnits,
              unit: rawMaterialUnit
            });
          }
        } else {
          // No inventory found - this is a shortage
          analysis.canMakeBatch = false;
          analysis.maxBatches = 0;
          analysis.shortages.push({
            name: rawMaterial.name,
            required: requiredInRawMaterialUnits,
            available: 0,
            shortage: requiredInRawMaterialUnits,
            unit: rawMaterialUnit,
            noInventory: true
          });
          ingredientAnalysis.canMake = 0;
        }

        analysis.ingredients.push(ingredientAnalysis);
      });

      if (analysis.shortages.length > 0) {
        analysis.canMakeBatch = false;
      }

      if (analysis.maxBatches === Infinity) {
        analysis.maxBatches = 999; // Cap at reasonable number for display
      }

      setBatchAnalysis(analysis);
    } catch (error) {
      console.error('Error calculating batch analysis:', error);
      setBatchAnalysis(null);
    }
  };

  const handleEdit = async () => {
    // Default to main location if creating new and no location set
    let defaultLocation = batch?.production_location || null;
    if (!defaultLocation && !batch) {
      const mainLocation = locations.find(loc => loc.is_main);
      if (mainLocation) {
        defaultLocation = mainLocation.id;
      }
    }
    
    setEditData({
      batch_number: batch?.batch_number || '',
      internal_code: batch?.internal_code || '',
      product_id: batch?.product_id || null,
      formulation_id: batch?.formulation_id || null,
      planned_quantity: batch?.planned_quantity || 0,
      planned_unit_of_measure: batch?.planned_unit_of_measure || 'oz',
      product_variant: batch?.product_variant || '16oz',
      product_variant_id: batch?.product_variant_id || null,
      units_to_make: batch?.units_to_make || 100,
      variant_distribution: batch?.variant_distribution || [],
      production_location: defaultLocation,
      manufacture_date: formatDateForInput(batch?.manufacture_date) || new Date().toISOString().split('T')[0],
      expiration_date: formatDateForInput(batch?.expiration_date) || '',
      status: batch?.status || 'draft',
      priority: batch?.priority || 'normal',
      production_line: batch?.production_line || '',
      shift_supervisor: batch?.shift_supervisor || '',
      production_notes: batch?.production_notes || '',
      planned_start_datetime: batch?.planned_start_date && batch?.planned_start_time 
        ? dayjs(`${batch.planned_start_date} ${batch.planned_start_time}`) 
        : null,
      planned_completion_datetime: batch?.planned_completion_date && batch?.planned_completion_time 
        ? dayjs(`${batch.planned_completion_date} ${batch.planned_completion_time}`) 
        : null,
      ambient_temperature_range: batch?.ambient_temperature_range || '',
      humidity_range: batch?.humidity_range || '',
      storage_conditions: batch?.storage_conditions || '',
      unit_configuration_id: batch?.unit_configuration_id || null
    });
    
    // Load variants if we have a product selected
    if (batch?.product_id) {
      const product = products.find(p => p.id === batch.product_id);
      setSelectedProduct(product);
      await fetchProductVariants(batch.product_id);
    }
    
    // Load formulation and set selectedFormulation if we have a formulation selected
    if (batch?.formulation_id) {
      const formulation = formulations.find(f => f.id === batch.formulation_id);
      setSelectedFormulation(formulation);
      await fetchFormulationIngredients(batch.formulation_id);
    }
    
    // Load inventory if we have a production location selected
    if (batch?.production_location) {
      await fetchRawMaterialsInventory(batch.production_location);
    }
    
    // Load unit configuration if we have one selected
    if (batch?.unit_configuration_id) {
      const unitConfig = unitConfigurations.find(config => config.id === batch.unit_configuration_id);
      setSelectedUnitConfig(unitConfig || null);
    } else {
      setSelectedUnitConfig(null);
    }
    
    setValidationError(''); // Clear any previous errors
    setActiveStep(0); // Reset to first step
    setEditDialogOpen(true);
  };

  useEffect(() => {
    if (isCreatingNew) {
      handleEdit();
    }
  }, [isCreatingNew]);

  useEffect(() => {
    fetchLocations();
    fetchUnitConfigurations();
    if (isCreatingNew) {
      fetchCalendarBatches();
      setSelectedDate(new Date());
    }
    loadUserProfile();
  }, [userCompanyId, isCreatingNew]);
  
  const loadUserProfile = async () => {
    try {
      if (!user?.id) return;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('batch_time_format_preference')
        .eq('id', user.id)
        .single();
        
      if (data && !error) {
        setUserProfile(data);
        setBatchTimeFormat(data.batch_time_format_preference || '12hour');
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      setBatchTimeFormat('12hour');
    }
  };
  
  const updateBatchTimeFormat = async (newFormat) => {
    try {
      if (!user?.id) return;
      
      const { error } = await supabase
        .from('profiles')
        .update({ batch_time_format_preference: newFormat })
        .eq('id', user.id);
        
      if (!error) {
        setBatchTimeFormat(newFormat);
        setUserProfile(prev => ({ ...prev, batch_time_format_preference: newFormat }));
      } else {
        console.error('Error updating batch time format:', error);
      }
    } catch (error) {
      console.error('Error updating batch time format:', error);
    }
  };

  // Load data for existing batch analysis
  useEffect(() => {
    if (batch && !isCreatingNew) {
      // Load formulation data for analysis
      if (batch.formulation_id) {
        const formulation = formulations.find(f => f.id === batch.formulation_id);
        setSelectedFormulation(formulation);
        fetchFormulationIngredients(batch.formulation_id);
      }
      
      // Load product data for analysis
      if (batch.product_id) {
        const product = products.find(p => p.id === batch.product_id);
        setSelectedProduct(product);
        fetchProductVariants(batch.product_id);
      }
      
      // Load inventory data for analysis
      if (batch.production_location) {
        fetchRawMaterialsInventory(batch.production_location);
        fetchLocationDetails(batch.production_location);
      }
    }
  }, [batch, formulations, products, isCreatingNew]);

  // Trigger batch analysis when data changes (for both main view and edit mode)
  useEffect(() => {
    const isEditMode = editDialogOpen;
    const currentData = isEditMode ? editData : batch;
    
    if (currentData && currentData.formulation_id && currentData.production_location && currentData.planned_quantity > 0) {
      calculateBatchAnalysis(isEditMode);
    } else {
      setBatchAnalysis(null);
    }
  }, [formulationIngredients, rawMaterialsInventory, batch?.planned_quantity, batch?.planned_unit_of_measure, editData.planned_quantity, editData.planned_unit_of_measure, editData.variant_distribution, selectedFormulation, editDialogOpen]);

  // Recalculate production values when productVariants load after autofill
  useEffect(() => {
    if (productVariants.length > 0 && editData.variant_distribution && editData.variant_distribution.length > 0 && editDialogOpen) {
      const totalProduction = calculateTotalProductionFromDistribution(editData.variant_distribution);
      if (totalProduction.totalVolume > 0) {
        setEditData(prev => ({
          ...prev,
          planned_quantity: totalProduction.totalVolume,
          planned_unit_of_measure: totalProduction.productionUnit,
          units_to_make: totalProduction.totalUnits
        }));
      }
    }
  }, [productVariants, editData.variant_distribution, editDialogOpen]);

  // Permission check for batch overview
  // For new batches: need both view AND edit permissions
  // For existing batches: only need view permission
  if (isCreatingNew ? (!canAccessPage('batch_overview') || !canEditPage('batch_overview')) : !canAccessPage('batch_overview')) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '70vh',
        textAlign: 'center',
        px: 3
      }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <FactoryIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
{isCreatingNew ? 'Batch Overview & Edit Permissions Required' : 'Batch Overview Permissions Required'}
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
{isCreatingNew 
                ? 'You need both view and edit permissions to create new batches. Contact your system administrator to request the necessary permissions.'
                : 'You don\'t currently have the necessary permissions to access the Batch Overview. Contact your system administrator to request access or verify your role permissions.'
              }
            </Typography>
          </Card>
      </Box>
    );
  }

  const handleProductChange = async (productId) => {
    const product = products.find(p => p.id === productId);
    setSelectedProduct(product);
    
    // Clear input values when product changes
    setVariantInputValues({});
    
    // Fetch variants for this product
    const variants = await fetchProductVariants(productId);
    
    // Set default variant (first one or create a default)
    let defaultVariant = null;
    let defaultUnits = 100;
    let totalQuantity = 0;
    let unitOfMeasure = 'oz';

    if (variants.length > 0) {
      // Use the first variant as default
      defaultVariant = variants[0];
      totalQuantity = (defaultVariant.volume_weight || 16) * defaultUnits;
      unitOfMeasure = defaultVariant.volume_unit || 'oz';
    } else {
      // No variants found, create a default 16oz
      totalQuantity = 16 * defaultUnits;
    }
    
    setEditData({ 
      ...editData, 
      product_id: productId,
      product_variant_id: defaultVariant?.id || null,
      product_variant: defaultVariant ? `${defaultVariant.volume_weight}${defaultVariant.volume_unit}` : '16oz',
      units_to_make: defaultUnits,
      planned_quantity: totalQuantity,
      planned_unit_of_measure: unitOfMeasure,
      formulation_id: '' // Reset formulation when product changes
    });
  };

  const handleVariantChange = (variantId) => {
    const variant = productVariants.find(v => v.id === variantId);
    if (!variant) return;
    
    const units = editData.units_to_make || 100;
    const totalQuantity = (variant.volume_weight || 16) * units;
    
    setEditData({
      ...editData,
      product_variant_id: variantId,
      product_variant: `${variant.volume_weight}${variant.volume_unit}`,
      planned_quantity: totalQuantity,
      planned_unit_of_measure: variant.volume_unit
    });
  };

  const handleUnitsChange = (units) => {
    const variantId = editData.product_variant_id;
    const variant = productVariants.find(v => v.id === variantId);
    const variantSize = variant?.volume_weight || 16;
    const unitOfMeasure = variant?.volume_unit || 'oz';
    const totalQuantity = variantSize * units;
    
    setEditData({
      ...editData,
      units_to_make: units,
      planned_quantity: totalQuantity,
      planned_unit_of_measure: unitOfMeasure
    });
  };

  const handleFormulationChange = async (formulationId) => {
    const formulation = formulations.find(f => f.id === formulationId);
    setSelectedFormulation(formulation);
    setEditData({ ...editData, formulation_id: formulationId });
    
    // Fetch formulation ingredients for batch analysis
    await fetchFormulationIngredients(formulationId);
  };

  // Product dialog handlers
  const handleOpenProductSelection = () => {
    setProductSearchTerm('');
    setDialogSelectedProduct(null);
    setProductSelectionDialogOpen(true);
  };

  const handleCloseProductSelection = () => {
    setProductSelectionDialogOpen(false);
    setProductSearchTerm('');
    setDialogSelectedProduct(null);
  };

  const handleConfirmProductSelection = async () => {
    if (dialogSelectedProduct) {
      // Only call handleProductChange if we haven't already set the product
      if (editData.product_id !== dialogSelectedProduct.id) {
        await handleProductChange(dialogSelectedProduct.id);
      }
      handleCloseProductSelection();
    }
  };

  const getFilteredProducts = () => {
    if (!productSearchTerm.trim()) return products;
    
    const searchTerm = productSearchTerm.toLowerCase();
    return products.filter(product => 
      product.name?.toLowerCase().includes(searchTerm) ||
      product.description?.toLowerCase().includes(searchTerm) ||
      product.sku?.toLowerCase().includes(searchTerm)
    );
  };

  // Formulation dialog handlers
  const handleOpenFormulationSelection = () => {
    setFormulationSearchTerm('');
    setFormulationSelectionDialogOpen(true);
  };

  const handleCloseFormulationSelection = () => {
    setFormulationSelectionDialogOpen(false);
    setFormulationSearchTerm('');
  };

  const handleSelectFormulation = async (formulation) => {
    await handleFormulationChange(formulation.id);
    handleCloseFormulationSelection();
  };

  const getFilteredFormulations = () => {
    if (!formulationSearchTerm.trim()) return formulations;
    
    const searchTerm = formulationSearchTerm.toLowerCase();
    const filtered = formulations.filter(formulation => 
      formulation.name?.toLowerCase().includes(searchTerm) ||
      formulation.version?.toLowerCase().includes(searchTerm) ||
      formulation.description?.toLowerCase().includes(searchTerm)
    );
    
    // Debug logging to see formulation structure
    if (filtered.length > 0) {
      console.log('Formulation data structure:', filtered[0]);
    }
    
    return filtered;
  };

  // Location dialog handlers
  const handleOpenLocationSelection = () => {
    setLocationSearchTerm('');
    setLocationSelectionDialogOpen(true);
  };

  const handleCloseLocationSelection = () => {
    setLocationSelectionDialogOpen(false);
    setLocationSearchTerm('');
  };

  const handleSelectLocation = async (location) => {
    setEditData({ ...editData, production_location: location.id });
    await fetchRawMaterialsInventory(location.id);
    await fetchLocationDetails(location.id);
    handleCloseLocationSelection();
  };

  const getFilteredLocations = () => {
    const filteredLocations = !locationSearchTerm.trim() 
      ? locations 
      : (() => {
          const searchTerm = locationSearchTerm.toLowerCase();
          return locations.filter(location => 
            location.name?.toLowerCase().includes(searchTerm) ||
            location.facility_type?.toLowerCase().includes(searchTerm) ||
            location.city?.toLowerCase().includes(searchTerm) ||
            location.state?.toLowerCase().includes(searchTerm)
          );
        })();
    
    // Ensure main location is always first, then alphabetically
    return filteredLocations.sort((a, b) => {
      if (a.is_main && !b.is_main) return -1;
      if (!a.is_main && b.is_main) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  };

  // Unit Configuration Selection Dialog handlers
  const handleOpenUnitConfigSelection = () => {
    setUnitConfigSearchTerm('');
    setUnitConfigSelectionDialogOpen(true);
  };

  const handleCloseUnitConfigSelection = () => {
    setUnitConfigSelectionDialogOpen(false);
    setUnitConfigSearchTerm('');
  };

  const handleSelectUnitConfig = (config) => {
    setSelectedUnitConfig(config);
    setUnitInputs({});
    handleCloseUnitConfigSelection();
  };

  const getFilteredUnitConfigurations = () => {
    if (!unitConfigSearchTerm.trim()) return unitConfigurations;
    
    const searchTerm = unitConfigSearchTerm.toLowerCase();
    return unitConfigurations.filter(config => 
      config.name?.toLowerCase().includes(searchTerm) ||
      config.base_unit_name?.toLowerCase().includes(searchTerm) ||
      config.case_unit_name?.toLowerCase().includes(searchTerm) ||
      config.pallet_unit_name?.toLowerCase().includes(searchTerm)
    );
  };

  const getTodaysCalendarBatches = () => {
    if (!selectedDate || !calendarBatches.length) return [];
    
    const today = selectedDate.toISOString().split('T')[0];
    return calendarBatches.filter(batch => 
      batch.planned_start_date === today
    );
  };

  const handleAutofillFromCalendar = async (calendarBatch) => {
    try {
      // First load product variants if product exists
      if (calendarBatch.product_id) {
        const product = products.find(p => p.id === calendarBatch.product_id);
        setSelectedProduct(product);
        await fetchProductVariants(calendarBatch.product_id);
      }
      
      // Then load formulation
      if (calendarBatch.formulation_id) {
        const formulation = formulations.find(f => f.id === calendarBatch.formulation_id);
        setSelectedFormulation(formulation);
        await fetchFormulationIngredients(calendarBatch.formulation_id);
      }
      
      // Load unit configuration if batch has one
      if (calendarBatch.unit_configuration_id) {
        const unitConfig = unitConfigurations.find(config => config.id === calendarBatch.unit_configuration_id);
        setSelectedUnitConfig(unitConfig || null);
      } else {
        setSelectedUnitConfig(null);
      }
      
      // Set the base edit data first
      setEditData(prev => ({
        ...prev,
        batch_number: calendarBatch.batch_number || '',
        product_id: calendarBatch.product_id || null,
        formulation_id: calendarBatch.formulation_id || null,
        production_location: calendarBatch.production_location || null,
        manufacture_date: calendarBatch.manufacture_date || '',
        expiration_date: calendarBatch.expiration_date || '',
        planned_start_datetime: calendarBatch.planned_start_date && calendarBatch.planned_start_time ? 
          dayjs(`${calendarBatch.planned_start_date} ${calendarBatch.planned_start_time}`) : null,
        planned_completion_datetime: calendarBatch.planned_completion_date && calendarBatch.planned_completion_time ? 
          dayjs(`${calendarBatch.planned_completion_date} ${calendarBatch.planned_completion_time}`) : null,
        status: calendarBatch.status === 'planned' ? 'scheduled' : calendarBatch.status || 'draft',
        priority: calendarBatch.priority || 'normal',
        production_line: calendarBatch.production_line || '',
        shift_supervisor: calendarBatch.shift_supervisor || '',
        variant_distribution: calendarBatch.variant_distribution || [],
        planned_quantity: calendarBatch.planned_quantity || 0,
        planned_unit_of_measure: calendarBatch.planned_unit_of_measure || 'oz',
        product_variant_id: calendarBatch.product_variant_id || null,
        product_variant: calendarBatch.product_variant || '',
        unit_configuration_id: calendarBatch.unit_configuration_id || null
      }));
      
      // Set variant input values if distribution exists
      if (calendarBatch.variant_distribution && calendarBatch.variant_distribution.length > 0) {
        const inputValues = {};
        calendarBatch.variant_distribution.forEach(variant => {
          inputValues[variant.variant_id] = variant.quantity;
        });
        setVariantInputValues(inputValues);
        
        // Also populate unit inputs if we have unit configuration
        if (calendarBatch.unit_configuration_id) {
          const unitConfig = unitConfigurations.find(config => config.id === calendarBatch.unit_configuration_id);
          if (unitConfig) {
            const calculatedInputs = {};
            
            // Calculate breakdown for EACH variant in the distribution
            calendarBatch.variant_distribution.forEach(variantDist => {
              const totalUnits = variantDist.quantity || 0;
              
              // Calculate breakdown from total units
              let remainingUnits = totalUnits;
              let pallets = 0;
              let cases = 0;
              let units = 0;
              
              if (unitConfig.enable_pallets && unitConfig.enable_cases) {
                const unitsPerPallet = unitConfig.cases_per_pallet * unitConfig.units_per_case;
                pallets = Math.floor(remainingUnits / unitsPerPallet);
                remainingUnits = remainingUnits % unitsPerPallet;
              }
              
              if (unitConfig.enable_cases) {
                cases = Math.floor(remainingUnits / unitConfig.units_per_case);
                remainingUnits = remainingUnits % unitConfig.units_per_case;
              }
              
              units = remainingUnits;
              
              // Set unit inputs for this variant
              calculatedInputs[variantDist.variant_id] = {
                pallets: pallets,
                cases: cases,
                units: units
              };
            });
            
            setUnitInputs(calculatedInputs);
          }
        }
      }
      
      // Fetch inventory for the location
      if (calendarBatch.production_location) {
        await fetchRawMaterialsInventory(calendarBatch.production_location);
        await fetchLocationDetails(calendarBatch.production_location);
      }
      
    } catch (error) {
      console.error('Error autofilling from calendar:', error);
    }
  };

  const calculateActualProductionVolumeEdit = () => {
    const totalProduction = calculateTotalProduction();
    if (!selectedFormulation || !formulationIngredients.length || !totalProduction.totalVolume) {
      return null;
    }
    
    const targetSize = totalProduction.totalVolume;
    const targetUnit = totalProduction.productionUnit;
    
    // Calculate weighted average density from formulation ingredients
    let calculatedDensity = 1.0; // Default cosmetic density
    
    if (formulationIngredients.length > 0) {
      let totalWeightedDensity = 0;
      let totalPercentage = 0;
      
      formulationIngredients.forEach(ingredient => {
        const rawMaterial = ingredient.raw_materials;
        const percentage = ingredient.percentage || 0;
        const materialDensity = rawMaterial?.density_g_ml;
        
        if (materialDensity && materialDensity > 0 && percentage > 0) {
          totalWeightedDensity += (materialDensity * percentage);
          totalPercentage += percentage;
        }
      });
      
      if (totalPercentage > 0) {
        calculatedDensity = totalWeightedDensity / totalPercentage;
      }
    }
    
    // Convert to gallons for display
    try {
      const targetUnitType = getUnitType(targetUnit);
      
      if (targetUnitType === 'weight') {
        // Convert weight to volume using unitConversions.js with density
        const targetInGallons = convertUnits(targetSize, targetUnit, 'gal', calculatedDensity);
        
        return {
          volume: targetInGallons,
          unit: 'gal',
          density: calculatedDensity,
          originalSize: targetSize,
          originalUnit: targetUnit
        };
      } else {
        // Already volume - convert directly to gallons
        const targetInGallons = convertUnits(targetSize, targetUnit, 'gal');
        
        return {
          volume: targetInGallons,
          unit: 'gal',
          density: calculatedDensity,
          originalSize: targetSize,
          originalUnit: targetUnit
        };
      }
    } catch (error) {
      console.warn('Volume conversion failed:', error);
      return null;
    }
  };

  const calculateFormulationScaling = () => {
    const totalProduction = calculateTotalProduction();
    if (!selectedFormulation || !totalProduction.totalVolume) return null;
    
    const formulationSize = selectedFormulation.target_batch_size || 1;
    const formulationUnit = normalizeProductVolumeUnit(selectedFormulation.unit_of_measure || 'gal');
    const productionUnit = normalizeProductVolumeUnit(productVariants[0]?.volume_unit || 'oz');
    
    // Calculate weighted average density from formulation ingredients
    let calculatedDensity = null;
    
    if (formulationIngredients.length > 0) {
      let totalWeightedDensity = 0;
      let totalPercentage = 0;
      
      formulationIngredients.forEach(ingredient => {
        const rawMaterial = ingredient.raw_materials;
        const percentage = ingredient.percentage || 0;
        const materialDensity = rawMaterial?.density_g_ml;
        
        if (materialDensity && materialDensity > 0 && percentage > 0) {
          totalWeightedDensity += (materialDensity * percentage);
          totalPercentage += percentage;
        }
      });
      
      if (totalPercentage > 0) {
        calculatedDensity = totalWeightedDensity / totalPercentage;
      }
    }
    
    // Convert formulation unit to production unit with proper handling
    let formulationInProductionUnits;
    let scalingFactor;
    let conversionUsed = 'direct';
    
    if (formulationUnit === productionUnit) {
      // Same units, direct conversion
      formulationInProductionUnits = formulationSize;
      conversionUsed = 'direct';
    } else {
      // Different units - check if conversion is possible
      const formulationUnitType = getUnitType(formulationUnit);
      const productionUnitType = getUnitType(productionUnit);
      
      if (formulationUnitType === productionUnitType) {
        // Same unit type (both volume or both weight) - direct conversion
        try {
          formulationInProductionUnits = convertUnits(
            formulationSize,
            formulationUnit,
            productionUnit,
            selectedFormulation
          );
          conversionUsed = 'direct';
        } catch (error) {
          console.warn('Unit conversion failed:', error);
          formulationInProductionUnits = formulationSize;
          conversionUsed = 'fallback';
        }
      } else {
        // Different unit types (volume vs weight) - need density
        const density = calculatedDensity || 1.0; // Use calculated density or default to 1.0 g/ml
        
        if (density && density > 0) {
          // Use density for conversion
          try {
            if (formulationUnitType === 'volume' && productionUnitType === 'weight') {
              // Convert formulation volume to weight using unitConversions.js with density
              formulationInProductionUnits = convertUnits(formulationSize, formulationUnit, productionUnit, density);
              conversionUsed = 'density';
            } else if (formulationUnitType === 'weight' && productionUnitType === 'volume') {
              // Convert formulation weight to volume using unitConversions.js with density
              formulationInProductionUnits = convertUnits(formulationSize, formulationUnit, productionUnit, density);
              conversionUsed = 'density';
            }
          } catch (error) {
            console.warn('Density conversion failed:', error);
            // Use default cosmetic density (1 g/ml) as fallback
            formulationInProductionUnits = formulationSize;
            conversionUsed = 'default_density';
          }
        } else {
          // No density available - use default cosmetic density (1 g/ml = 1 kg/L)
          try {
            if (formulationUnitType === 'volume' && productionUnitType === 'weight') {
              // Use 1.0 g/ml default density for cosmetics with unitConversions.js
              formulationInProductionUnits = convertUnits(formulationSize, formulationUnit, productionUnit, 1.0);
              conversionUsed = 'default_density';
            } else if (formulationUnitType === 'weight' && productionUnitType === 'volume') {
              // Use 1.0 g/ml default density for cosmetics with unitConversions.js
              formulationInProductionUnits = convertUnits(formulationSize, formulationUnit, productionUnit, 1.0);
              conversionUsed = 'default_density';
            }
          } catch (error) {
            console.warn('Default density conversion failed:', error);
            formulationInProductionUnits = formulationSize;
            conversionUsed = 'error';
          }
        }
      }
    }
    
    // Calculate scaling factor
    scalingFactor = totalProduction.totalVolume / formulationInProductionUnits;
    
    const remainderInProductionUnits = totalProduction.totalVolume % formulationInProductionUnits;
    let remainderInFormulationUnits;
    
    try {
      remainderInFormulationUnits = remainderInProductionUnits / scalingFactor;
    } catch (error) {
      remainderInFormulationUnits = 0;
    }
    
    return {
      formulationSize,
      formulationUnit,
      originalFormulationUnit: selectedFormulation.unit_of_measure || 'gal',
      formulationInProductionUnits,
      productionUnit,
      originalProductionUnit: productVariants[0]?.volume_unit || 'oz',
      scalingFactor,
      batchesToMake: Math.floor(scalingFactor),
      remainderInProductionUnits,
      remainderInFormulationUnits,
      conversionUsed,
      density: calculatedDensity || 1.0,
      densitySource: calculatedDensity ? 'calculated_from_ingredients' : 'default_cosmetic'
    };
  };

  const getVariantQuantity = (variantId) => {
    const distribution = editData.variant_distribution || [];
    const entry = distribution.find(d => d.variant_id === variantId);
    return entry ? entry.quantity : 0;
  };

  const getVariantInputValue = (variantId) => {
    // Return the local input value if it exists, otherwise return the actual quantity
    if (variantInputValues.hasOwnProperty(variantId)) {
      return variantInputValues[variantId];
    }
    return getVariantQuantity(variantId) || '';
  };

  const handleVariantInputChange = (variantId, inputValue) => {
    // Update the local input value for immediate display
    setVariantInputValues({
      ...variantInputValues,
      [variantId]: inputValue
    });
    
    // Update the actual distribution
    handleVariantDistributionChange(variantId, inputValue);
  };

  const handleVariantDistributionChange = (variantId, inputValue) => {
    // Parse the input value, ensuring it's not negative
    let quantity = 0;
    
    if (inputValue === '' || inputValue === null || inputValue === undefined) {
      quantity = 0;
    } else {
      const parsed = parseInt(inputValue, 10);
      quantity = isNaN(parsed) ? 0 : Math.max(0, parsed); // Ensure non-negative
    }
    
    const currentDistribution = editData.variant_distribution || [];
    const existingIndex = currentDistribution.findIndex(d => d.variant_id === variantId);
    
    let newDistribution;
    if (existingIndex >= 0) {
      // Update existing entry
      newDistribution = [...currentDistribution];
      if (quantity > 0) {
        newDistribution[existingIndex] = { variant_id: variantId, quantity };
      } else {
        // Remove entry if quantity is 0
        newDistribution.splice(existingIndex, 1);
      }
    } else if (quantity > 0) {
      // Add new entry
      newDistribution = [...currentDistribution, { variant_id: variantId, quantity }];
    } else {
      // No change needed for 0 quantity on non-existing entry
      newDistribution = currentDistribution;
    }
    
    const totalProduction = calculateTotalProductionFromDistribution(newDistribution);
    
    setEditData({
      ...editData,
      variant_distribution: newDistribution,
      planned_quantity: totalProduction.totalVolume,
      planned_unit_of_measure: totalProduction.productionUnit,
      units_to_make: totalProduction.totalUnits
    });
  };

  const calculateTotalProduction = () => {
    const distribution = editData.variant_distribution || [];
    return calculateTotalProductionFromDistribution(distribution);
  };

  const calculateTotalProductionFromDistribution = (distribution) => {
    let totalUnits = 0;
    let totalVolume = 0;
    
    // Calculate total volume/weight - all 'oz' units are weight ounces (consistent with unit system)
    distribution.forEach(entry => {
      const variant = productVariants.find(v => v.id === entry.variant_id);
      if (variant) {
        totalUnits += entry.quantity;
        totalVolume += entry.quantity * variant.volume_weight;
      }
    });
    
    const productionUnit = normalizeProductVolumeUnit(productVariants[0]?.volume_unit || 'oz');
    
    return { 
      totalUnits, 
      totalVolume,
      productionUnit
    };
  };

  const calculateTotalUnitsFromInputs = (variantId, inputsOverride = null) => {
    if (!selectedUnitConfig || !variantId) return 0;
    
    const variantInputs = inputsOverride || unitInputs[variantId] || { pallets: 0, cases: 0, units: 0 };
    let total = 0;
    
    // Add units from pallets
    if (selectedUnitConfig.enable_pallets && selectedUnitConfig.enable_cases) {
      total += (variantInputs.pallets || 0) * selectedUnitConfig.cases_per_pallet * selectedUnitConfig.units_per_case;
    }
    
    // Add units from cases
    if (selectedUnitConfig.enable_cases) {
      total += (variantInputs.cases || 0) * selectedUnitConfig.units_per_case;
    }
    
    // Add individual units
    total += (variantInputs.units || 0);
    
    return total;
  };

  const updateVariantFromUnitInputs = (variantId, newInputs) => {
    if (!selectedUnitConfig) return;
    
    const totalUnits = calculateTotalUnitsFromInputs(variantId, newInputs);
    
    // Update only this specific variant in the distribution
    const currentDistribution = editData.variant_distribution || [];
    let newDistribution = [...currentDistribution];
    
    // Find existing entry for this variant
    const existingIndex = newDistribution.findIndex(d => d.variant_id === variantId);
    
    if (totalUnits > 0) {
      if (existingIndex >= 0) {
        // Update existing entry
        newDistribution[existingIndex] = { variant_id: variantId, quantity: totalUnits };
      } else {
        // Add new entry
        newDistribution.push({ variant_id: variantId, quantity: totalUnits });
      }
    } else {
      // Remove entry if quantity is 0
      if (existingIndex >= 0) {
        newDistribution.splice(existingIndex, 1);
      }
    }
    
    const totalProduction = calculateTotalProductionFromDistribution(newDistribution);
    
    setEditData({
      ...editData,
      variant_distribution: newDistribution,
      planned_quantity: totalProduction.totalVolume,
      planned_unit_of_measure: totalProduction.productionUnit,
      units_to_make: totalProduction.totalUnits
    });
  };

  // Unit Configuration Management Functions
  const getConfigDescription = (config) => {
    if (!config.enable_cases && !config.enable_pallets) {
      return `${config.base_unit_name} only`;
    } else if (config.enable_cases && !config.enable_pallets) {
      return `${config.base_unit_name} → ${config.case_unit_name}`;
    } else {
      return `${config.base_unit_name} → ${config.case_unit_name} → ${config.pallet_unit_name}`;
    }
  };

  const handleUnitConfigSubmit = async () => {
    setSavingUnitConfig(true);
    
    try {
      if (!unitConfigFormData.name) {
        throw new Error('Please enter a configuration name');
      }

      if (unitConfigFormData.enable_cases && unitConfigFormData.units_per_case < 1) {
        throw new Error('Units per case must be at least 1');
      }

      if (unitConfigFormData.enable_pallets && unitConfigFormData.cases_per_pallet < 1) {
        throw new Error('Cases per pallet must be at least 1');
      }

      const configData = {
        company_id: userCompanyId,
        name: unitConfigFormData.name,
        base_unit_name: unitConfigFormData.base_unit_name,
        enable_cases: unitConfigFormData.enable_cases,
        case_unit_name: unitConfigFormData.enable_cases ? unitConfigFormData.case_unit_name : null,
        units_per_case: unitConfigFormData.enable_cases ? parseInt(unitConfigFormData.units_per_case) : 1,
        enable_pallets: unitConfigFormData.enable_pallets,
        pallet_unit_name: unitConfigFormData.enable_pallets ? unitConfigFormData.pallet_unit_name : null,
        cases_per_pallet: unitConfigFormData.enable_pallets ? parseInt(unitConfigFormData.cases_per_pallet) : 1
      };

      if (editingUnitConfig) {
        const { error } = await supabase
          .from('unit_configurations')
          .update(configData)
          .eq('id', editingUnitConfig.id);

        if (error) throw error;
        showSnackbar('Unit configuration updated successfully', 'success');
      } else {
        const { error } = await supabase
          .from('unit_configurations')
          .insert([configData]);

        if (error) throw error;
        showSnackbar('Unit configuration created successfully', 'success');
      }

      handleCloseUnitConfigDialog();
      await fetchUnitConfigurations();
    } catch (err) {
      console.error('Error saving unit configuration:', err);
      showSnackbar(err.message || 'Failed to save unit configuration', 'error');
    } finally {
      setSavingUnitConfig(false);
    }
  };

  const handleCloseUnitConfigDialog = () => {
    setUnitConfigDialogOpen(false);
    setEditingUnitConfig(null);
    setUnitConfigFormData({
      name: '',
      base_unit_name: 'units',
      enable_cases: true,
      case_unit_name: 'cases',
      units_per_case: 12,
      enable_pallets: true,
      pallet_unit_name: 'pallets',
      cases_per_pallet: 42
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setValidationError(''); // Clear previous errors
    
    try {
      // Validate required fields with user-friendly error messages
      const requiredFields = [];
      
      if (!editData.batch_number?.trim()) requiredFields.push('Batch Number');
      if (!editData.status) requiredFields.push('Status');
      if (!editData.product_id) requiredFields.push('Product');
      if (!editData.formulation_id) requiredFields.push('Formulation');
      if (!editData.production_location) requiredFields.push('Production Location');
      if (!editData.manufacture_date) requiredFields.push('Manufacture Date');
      if (!editData.expiration_date) requiredFields.push('Expiration Date');
      if (!editData.planned_start_datetime) requiredFields.push('Planned Start Date & Time');
      if (!editData.planned_completion_datetime) requiredFields.push('Planned Completion Date & Time');
      if (!editData.priority) requiredFields.push('Priority');
      if (!editData.production_line?.trim()) requiredFields.push('Tank #');
      if (!editData.shift_supervisor?.trim()) requiredFields.push('Shift Supervisor');
      if (!editData.variant_distribution || editData.variant_distribution.length === 0) {
        requiredFields.push('Product Variants (at least one variant must be configured)');
      } else if (!editData.variant_distribution.some(v => v.quantity > 0)) {
        requiredFields.push('Product Variant Quantities (at least one variant must have quantity > 0)');
      }

      if (requiredFields.length > 0) {
        const errorMessage = `Please fill in the following required fields:\n• ${requiredFields.join('\n• ')}`;
        setValidationError(errorMessage);
        showSnackbar(errorMessage, 'error');
        setSaving(false);
        return;
      }

      // Clean up date fields - convert empty strings to null
      const cleanedData = { ...editData };
      if (cleanedData.expiration_date === '') cleanedData.expiration_date = null;
  
      // Validate all date relationships
      if (cleanedData.expiration_date && cleanedData.manufacture_date) {
        const mfgDate = parseLocalDate(cleanedData.manufacture_date);
        const expDate = parseLocalDate(cleanedData.expiration_date);
        if (mfgDate && expDate && expDate <= mfgDate) {
          setValidationError('Expiration date must be AFTER manufacture date (not the same day)');
          setSaving(false);
          return;
        }
      }
      
      if (cleanedData.planned_start_datetime && cleanedData.manufacture_date) {
        const mfgDate = parseLocalDate(cleanedData.manufacture_date);
        const startDate = cleanedData.planned_start_datetime.startOf('day');
        if (mfgDate && startDate.isBefore(dayjs(mfgDate))) {
          setValidationError('Planned start date cannot be before manufacture date');
          setSaving(false);
          return;
        }
      }
      
      if (cleanedData.planned_completion_datetime && cleanedData.planned_start_datetime) {
        const startDateTime = cleanedData.planned_start_datetime;
        const completionDateTime = cleanedData.planned_completion_datetime;
        if (startDateTime && completionDateTime && completionDateTime.isBefore(startDateTime)) {
          setValidationError('Planned completion date & time cannot be before planned start date & time');
          setSaving(false);
          return;
        }
      }
      
      // Calculate and store production volume
      const actualVolume = calculateActualProductionVolumeEdit();
      
      // Calculate unit breakdown for storage
      let palletQuantity = 0;
      let caseQuantity = 0; 
      let unitQuantity = 0;
      
      if (selectedUnitConfig && cleanedData.units_to_make > 0) {
        let remainingUnits = cleanedData.units_to_make;
        
        // Calculate pallets if enabled
        if (selectedUnitConfig.enable_pallets && selectedUnitConfig.enable_cases) {
          const unitsPerPallet = selectedUnitConfig.cases_per_pallet * selectedUnitConfig.units_per_case;
          palletQuantity = parseFloat((remainingUnits / unitsPerPallet).toFixed(2));
          const fullPallets = Math.floor(remainingUnits / unitsPerPallet);
          remainingUnits = remainingUnits % unitsPerPallet;
        }
        
        // Calculate cases if enabled
        if (selectedUnitConfig.enable_cases) {
          caseQuantity = Math.floor(remainingUnits / selectedUnitConfig.units_per_case);
          remainingUnits = remainingUnits % selectedUnitConfig.units_per_case;
        }
        
        // Remaining individual units
        unitQuantity = remainingUnits;
      }
      
      const batchData = {
        ...cleanedData,
        planned_start_date: cleanedData.planned_start_datetime ? cleanedData.planned_start_datetime.format('YYYY-MM-DD') : null,
        planned_start_time: cleanedData.planned_start_datetime ? cleanedData.planned_start_datetime.format('HH:mm') : null,
        planned_completion_date: cleanedData.planned_completion_datetime ? cleanedData.planned_completion_datetime.format('YYYY-MM-DD') : null,
        planned_completion_time: cleanedData.planned_completion_datetime ? cleanedData.planned_completion_datetime.format('HH:mm') : null,
        calculated_production_volume: actualVolume?.volume || null,
        calculated_production_unit: actualVolume?.unit || 'gal',
        calculated_density: actualVolume?.density || null,
        // Ensure all UUID fields are null instead of empty strings
        product_id: cleanedData.product_id || null,
        formulation_id: cleanedData.formulation_id || null,
        production_location: cleanedData.production_location || null,
        unit_configuration_id: selectedUnitConfig?.id || null,
        product_variant_id: cleanedData.product_variant_id || null,
        pallet_quantity: palletQuantity,
        case_quantity: caseQuantity,
        unit_quantity: unitQuantity,
        company_id: userCompanyId,
        updated_by: user?.id,
        updated_at: new Date().toISOString()
      };
      
      delete batchData.planned_start_datetime;
      delete batchData.planned_completion_datetime;

      if (isCreatingNew) {
        batchData.created_by = user?.id;
        batchData.created_at = new Date().toISOString();
      }

      let result;
      if (batch?.id) {
        const { data, error } = await supabase
          .from('production_batches')
          .update(batchData)
          .eq('id', batch.id)
          .select()
          .single();
        
        if (error) throw error;
        result = data;
      } else {
        const { data, error } = await supabase
          .from('production_batches')
          .insert([batchData])
          .select()
          .single();
        
        if (error) throw error;
        result = data;
      }

      setEditDialogOpen(false);
      setVariantInputValues({}); // Clear input values after successful save
      onUpdate(result.id);
      showSnackbar(`Batch ${isCreatingNew ? 'created' : 'updated'} successfully!`, 'success');
    } catch (error) {
      console.error('Error saving batch:', error);
      
      // Provide user-friendly error messages instead of raw database errors
      let errorMessage = `Error ${isCreatingNew ? 'creating' : 'updating'} batch: `;
      
      if (error.message.includes('duplicate key') || error.message.includes('unique constraint')) {
        errorMessage += 'This batch conflicts with an existing record. Please check your entries.';
      } else if (error.message.includes('foreign key') || error.message.includes('violates foreign key constraint')) {
        errorMessage += 'Invalid selection detected. Please ensure all selected items (product, formulation, location) are valid.';
      } else if (error.message.includes('check constraint') || error.message.includes('violates check constraint')) {
        errorMessage += 'Invalid data detected. Please check all your entries are within acceptable ranges.';
      } else if (error.message.includes('permission denied') || error.message.includes('insufficient_privilege')) {
        errorMessage += 'You do not have permission to perform this action. Please contact your administrator.';
      } else if (error.message.includes('connection')) {
        errorMessage += 'Connection error. Please check your internet connection and try again.';
      } else {
        // For other unexpected errors, still show the original message but in a cleaner format
        errorMessage += 'An unexpected error occurred. Please try again or contact support if the problem persists.';
      }
      
      setValidationError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditDialogOpen(false);
    setActiveStep(0);
    setVariantInputValues({}); // Clear input values
    if (isCreatingNew && onBackToList) {
      // Go back to list if canceling new batch creation
      onBackToList();
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step) => {
    setActiveStep(step);
  };

  const steps = [
    'Basic Information',
    'Production Planning',
    'Packaging & Quantities',
    'Dates & Schedule',
    'Production Details'
  ];

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      pending_approval: 'warning',
      approved: 'info',
      scheduled: 'primary',
      in_production: 'secondary',
      quality_testing: 'warning',
      completed: 'success',
      on_hold: 'warning',
      cancelled: 'error',
      failed: 'error'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'default',
      normal: 'primary',
      high: 'warning',
      urgent: 'error'
    };
    return colors[priority] || 'default';
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
  };

  const getLocationLabel = (locationId) => {
    const location = locations.find(loc => loc.id === locationId);
    if (location) {
      return location.name;
    }
    return locationId || 'Not set';
  };

  const calculateActualProductionVolume = () => {
    if (!selectedFormulation || !formulationIngredients.length) {
      return null;
    }
    
    const currentData = editDialogOpen ? editData : batch;
    
    // Calculate from variant distribution if available, otherwise use planned_quantity
    let targetSize = 0;
    let targetUnit = 'oz';
    
    if (currentData?.variant_distribution && currentData.variant_distribution.length > 0) {
      // Calculate total from variant distribution
      currentData.variant_distribution.forEach(variant => {
        const productVariant = productVariants.find(v => v.id === variant.variant_id);
        if (productVariant) {
          targetSize += variant.quantity * productVariant.volume_weight;
          targetUnit = productVariant.volume_unit;
        }
      });
    } else {
      // Fallback to planned_quantity
      targetSize = currentData?.planned_quantity || 0;
      targetUnit = currentData?.planned_unit_of_measure || 'oz';
    }
    
    // Calculate weighted average density from formulation ingredients (same as batch analysis)
    let calculatedDensity = 1.0; // Default cosmetic density
    
    if (formulationIngredients.length > 0) {
      let totalWeightedDensity = 0;
      let totalPercentage = 0;
      
      formulationIngredients.forEach(ingredient => {
        const rawMaterial = ingredient.raw_materials;
        const percentage = ingredient.percentage || 0;
        const materialDensity = rawMaterial?.density_g_ml;
        
        if (materialDensity && materialDensity > 0 && percentage > 0) {
          totalWeightedDensity += (materialDensity * percentage);
          totalPercentage += percentage;
        }
      });
      
      if (totalPercentage > 0) {
        calculatedDensity = totalWeightedDensity / totalPercentage;
      }
    }
    
    // Convert to gallons for display
    try {
      const targetUnitType = getUnitType(targetUnit);
      
      if (targetUnitType === 'weight') {
        // Convert weight to volume using unitConversions.js with density
        const targetInGallons = convertUnits(targetSize, targetUnit, 'gal', calculatedDensity);
        
        return {
          volume: targetInGallons,
          unit: 'gal',
          density: calculatedDensity,
          originalSize: targetSize,
          originalUnit: targetUnit
        };
      } else {
        // Already volume - convert directly to gallons
        const targetInGallons = convertUnits(targetSize, targetUnit, 'gal');
        
        return {
          volume: targetInGallons,
          unit: 'gal',
          density: calculatedDensity,
          originalSize: targetSize,
          originalUnit: targetUnit
        };
      }
    } catch (error) {
      console.warn('Volume conversion failed:', error);
      return null;
    }
  };



  const getStatusIcon = (status) => {
    return null;
  };

  const getPriorityIcon = (priority) => {
    return null;
  };

  if (!batch && !isCreatingNew) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          No batch data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Enhanced Header Section */}
      <Card 
        sx={{ 
          borderRadius: 3,
          mb: 4,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: 'white',
          backdropFilter: 'blur(20px)',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backdropFilter: 'blur(10px)'
                }}>
                  <FactoryIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                    {batch?.batch_number || 'New Production Batch'}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {batch?.product?.name || 'Product not selected'}
                  </Typography>
                </Box>
              </Stack>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Time</InputLabel>
                <Select
                  value={batchTimeFormat}
                  onChange={(e) => updateBatchTimeFormat(e.target.value)}
                  label="Time"
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1)
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <MenuItem value="12hour">12 Hour</MenuItem>
                  <MenuItem value="24hour">24 Hour</MenuItem>
                </Select>
              </FormControl>
              {canEditPage('batch_overview') && (
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                  size="large"
                  sx={{
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    background: `linear-gradient(${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    fontWeight: 500,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      background: `linear-gradient(${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                    },
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  Edit Batch
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* 3-Panel Layout */}
      <Grid container spacing={5}>
        {/* PANEL A: Batch Info Card */}
        <Grid item xs={12} lg={4}>
          <Card 
            sx={{ 
              height: 'fit-content',
              borderRadius: 3,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: `white`,
              backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backdropFilter: 'blur(10px)'
                }}>
                  <AssignmentIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2 }}>
                  Batch Information
                </Typography>
              </Box>
              
              <Stack spacing={3}>
                {/* Core Information - Always Visible */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 2, display: 'block' }}>
                    CORE INFORMATION
                  </Typography>
                  <Stack spacing={2}>
                    <Chip 
                      icon={<LocalDrinkIcon />}
                      label={`Batch: ${batch?.batch_number || 'Not set'}`}
                      variant="outlined"
                      sx={{ 
                        justifyContent: 'flex-start', 
                        fontSize: '0.875rem', 
                        py: 1,
                        borderRadius: 2,
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        color: theme.palette.primary.main,
                        background: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        '& .MuiChip-icon': { color: theme.palette.primary.main },
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                        },
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    />
                    <Stack direction="row" spacing={1}>
                      <Chip 
                        label={`Status: ${formatStatus(batch?.status)}`}
                        sx={{ 
                          fontWeight: 500,
                          borderRadius: 2,
                          background: `linear-gradient(${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                          color: 'white',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                          '&:hover': {
                            background: `linear-gradient(${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                          },
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                      />
                      <Chip 
                        label={`Priority: ${batch?.priority?.toUpperCase() || 'NORMAL'}`}
                        variant="outlined"
                        sx={{ 
                          fontWeight: 500,
                          borderRadius: 2,
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                          color: theme.palette.primary.main,
                          background: 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.04),
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                          },
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                      />
                    </Stack>
                  </Stack>
                </Box>

                                {/* Collapsible Identification Details */}
                {batch?.internal_code && (
                  <Accordion 
                    expanded={accordionStates.additionalIdentifiers}
                    onChange={(event, expanded) => updateAccordionState('additionalIdentifiers', expanded)}
                    elevation={0}
                    sx={{
                      mt: 3,
                      mb: 3,
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.grey[400]}`,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(20px)',
                      overflow: 'hidden',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      '&:before': {
                        display: 'none'
                      },
                      '&.Mui-expanded': {
                        margin: '24px 0 24px 0'
                      },
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      borderRadius: '12px 12px 0 0',
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      border: 'none',
                      minHeight: 72,
                      '&.Mui-expanded': {
                        minHeight: 72,
                        borderRadius: '12px 12px 0 0'
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: '16px 0',
                        alignItems: 'center',
                        '&.Mui-expanded': {
                          margin: '16px 0'
                        }
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                      Additional Identifiers
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 4 }}>
                    <Stack spacing={1.5}>
                      {batch?.internal_code && (
                        <Chip 
                          icon={<AssignmentIcon />}
                          label={`Code: ${batch.internal_code}`}
                          variant="outlined"
                          sx={{ 
                            justifyContent: 'flex-start', 
                            fontSize: '0.875rem', 
                            py: 1,
                            borderRadius: 2,
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            color: theme.palette.primary.main,
                            background: 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(10px)',
                            '& .MuiChip-icon': { color: theme.palette.primary.main },
                            '&:hover': {
                              borderColor: theme.palette.primary.main,
                              backgroundColor: alpha(theme.palette.primary.main, 0.04),
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                            },
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                        />
                      )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Product & Formulation */}
                <Accordion 
                  expanded={accordionStates.productFormulation}
                  onChange={(event, expanded) => updateAccordionState('productFormulation', expanded)}
                  elevation={0}
                  sx={{
                    mt: 3,
                    mb: 3,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(20px)',
                    overflow: 'hidden',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      margin: '24px 0 24px 0'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      borderRadius: '8px 8px 0 0',
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      border: 'none',
                      minHeight: 56,
                      '&.Mui-expanded': {
                        minHeight: 56,
                        borderRadius: '8px 8px 0 0'
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                        alignItems: 'center',
                        '&.Mui-expanded': {
                          margin: '12px 0'
                        }
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                      Product & Formulation
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box>
                                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>
                            PRODUCT
                          </Typography>
                          <Paper sx={{ 
                            p: 2, 
                            background: `white`,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                            backdropFilter: 'blur(10px)',
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`,
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}>
                            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.primary }}>
                              {batch?.product?.name || 'Not selected'}
                            </Typography>
                            {batch?.product_variant && batch?.units_to_make && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                {batch.units_to_make} units × {batch.product_variant}
                              </Typography>
                            )}
                          </Paper>
                      </Box>
                      
                                              <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>
                            FORMULATION
                          </Typography>
                          <Paper sx={{ 
                            p: 2, 
                            background: `white`,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                            backdropFilter: 'blur(10px)',
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`,
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}>
                            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5, color: theme.palette.text.primary }}>
                              {batch?.formulation?.name || 'Not selected'}
                            </Typography>
                            {batch?.formulation?.version && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Version: {batch.formulation.version}
                              </Typography>
                            )}
                          </Paper>
                        </Box>
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                {/* Collapsible Location & Dates */}
                <Accordion 
                  expanded={accordionStates.locationSchedule}
                  onChange={(event, expanded) => updateAccordionState('locationSchedule', expanded)}
                  elevation={0}
                  sx={{
                    mt: 3,
                    mb: 3,
                    borderRadius: '12px !important',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(20px)',
                    overflow: 'hidden',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      margin: '24px 0 24px 0',
                      borderRadius: '12px !important'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiAccordion-region': {
                      borderRadius: '0 0 12px 12px !important'
                    }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      border: 'none',
                      minHeight: 56,
                      '&.Mui-expanded': {
                        minHeight: 56
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                        alignItems: 'center',
                        '&.Mui-expanded': {
                          margin: '12px 0'
                        }
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                      Location & Schedule
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3 }}>
                    <Stack spacing={2}>
                                              <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>
                            PRODUCTION LOCATION
                          </Typography>
                        <Chip 
                          icon={<FactoryIcon />}
                          label={batch?.production_location ? getLocationLabel(batch.production_location) : 'Not set'}
                          variant="outlined"
                          sx={{ 
                            justifyContent: 'flex-start', 
                            fontSize: '0.875rem', 
                            py: 1.5, 
                            px: 2,
                            width: '100%',
                            borderRadius: 2,
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            color: theme.palette.primary.main,
                            background: 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(10px)',
                            '& .MuiChip-icon': { 
                              color: theme.palette.primary.main,
                              mr: 1.5,
                              fontSize: '1.1rem'
                            },
                            '&:hover': {
                              borderColor: theme.palette.primary.main,
                              backgroundColor: alpha(theme.palette.primary.main, 0.04),
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                            },
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                        />
                      </Box>
                      
                                              <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>
                            KEY DATES
                          </Typography>
                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Manufacture:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatDateForDisplay(batch?.manufacture_date)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Expires:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatDateForDisplay(batch?.expiration_date)}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>

                        {/* Location Details Section */}
                        {batch && batch.production_location && locationCapacities.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 2, display: 'block' }}>
                              LOCATION DETAILS
                            </Typography>

                            {/* Product Capacities Section */}
                            {locationCapacities.length > 0 && (
                              <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 2, color: theme.palette.primary.main }}>
                                  Product Capacities
                                </Typography>
                                <TableContainer sx={{ 
                                  borderRadius: 2,
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                                  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                                  overflow: 'hidden'
                                }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ 
                                          fontWeight: 500,
                                          fontSize: '0.75rem',
                                          bgcolor: 'white',
                                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                                          color: 'primary.main',
                                          py: 1,
                                          px: 2
                                        }}>
                                          Product
                                        </TableCell>
                                        <TableCell sx={{ 
                                          fontWeight: 500,
                                          fontSize: '0.75rem',
                                          bgcolor: 'white',
                                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                                          color: 'primary.main',
                                          py: 1,
                                          px: 2
                                        }}>
                                          Capacity
                                        </TableCell>
                                        <TableCell sx={{ 
                                          fontWeight: 500,
                                          fontSize: '0.75rem',
                                          bgcolor: 'white',
                                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                                          color: 'primary.main',
                                          py: 1,
                                          px: 2
                                        }}>
                                          Setup Time
                                        </TableCell>
                                        <TableCell sx={{ 
                                          fontWeight: 500,
                                          fontSize: '0.75rem',
                                          bgcolor: 'white',
                                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                                          color: 'primary.main',
                                          py: 1,
                                          px: 2
                                        }}>
                                          Cleanup Time
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {(capacitiesRowsPerPage > 0
                                        ? locationCapacities.slice(capacitiesPage * capacitiesRowsPerPage, capacitiesPage * capacitiesRowsPerPage + capacitiesRowsPerPage)
                                        : locationCapacities
                                      ).map((capacity) => (
                                        <TableRow key={capacity.id} hover sx={{ 
                                          cursor: 'default',
                                          '&:hover': { 
                                            bgcolor: 'grey.50',
                                            transform: 'translateX(2px)',
                                            transition: 'all 0.2s ease'
                                          },
                                          '& .MuiTableCell-root': {
                                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                            py: 1.5,
                                            px: 2
                                          }
                                        }}>
                                          <TableCell>
                                            <Typography variant="body2" sx={{ 
                                              fontWeight: 500,
                                              color: 'text.primary',
                                              fontSize: '0.7rem'
                                            }}>
                                              {capacity.product?.name || 'Unknown Product'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2" sx={{ 
                                              fontWeight: 500,
                                              color: 'text.primary',
                                              fontSize: '0.7rem'
                                            }}>
                                              {capacity.capacity}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2" sx={{ 
                                              fontWeight: 500,
                                              color: 'text.primary',
                                              fontSize: '0.7rem'
                                            }}>
                                              {capacity.setup_time} min
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2" sx={{ 
                                              fontWeight: 500,
                                              color: 'text.primary',
                                              fontSize: '0.7rem'
                                            }}>
                                              {capacity.cleanup_time} min
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                                <Box sx={{ 
                                  borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                                  borderRadius: '0 0 8px 8px'
                                }}>
                                  <TablePagination
                                    rowsPerPageOptions={[5, 10, 15]}
                                    component="div"
                                    count={locationCapacities.length}
                                    rowsPerPage={capacitiesRowsPerPage}
                                    page={capacitiesPage}
                                    onPageChange={(event, newPage) => setCapacitiesPage(newPage)}
                                    onRowsPerPageChange={(event) => {
                                      setCapacitiesRowsPerPage(parseInt(event.target.value, 10));
                                      setCapacitiesPage(0);
                                    }}
                                    sx={{ 
                                      px: 2,
                                      py: 1,
                                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        color: theme.palette.text.primary
                                      },
                                      '& .MuiTablePagination-select': {
                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                        borderRadius: 1,
                                        px: 1,
                                        py: 0.5,
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                        background: 'white',
                                        '&:hover': {
                                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                        }
                                      },
                                      '& .MuiTablePagination-actions': {
                                        '& .MuiIconButton-root': {
                                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                          borderRadius: 1,
                                          margin: '0 1px',
                                          background: 'white',
                                          color: theme.palette.primary.main,
                                          fontWeight: 500,
                                          width: 24,
                                          height: 24,
                                          transition: 'all 0.2s ease',
                                          '&:hover': {
                                            background: theme.palette.primary.main,
                                            color: 'white',
                                            transform: 'translateY(-1px)',
                                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                                          },
                                          '&.Mui-disabled': {
                                            background: alpha(theme.palette.grey[300], 0.5),
                                            color: theme.palette.grey[500],
                                            border: `1px solid ${theme.palette.grey[300]}`,
                                            transform: 'none',
                                            boxShadow: 'none'
                                          }
                                        }
                                      }
                                    }}
                                  />
                                </Box>
                              </Box>
                            )}
                          </Box>
                        )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* PANEL B: Scaling + Inventory Intelligence */}
        <Grid item xs={12} lg={5}>
          <Stack spacing={3}>
            {/* Production Size Card */}
            <Card key={`production-${editData?.variant_distribution?.length || 0}-${editData?.planned_quantity || 0}`} sx={{ 
              borderRadius: 3,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: `white`,
              backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    backdropFilter: 'blur(10px)'
                  }}>
                    <ScaleIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2 }}>
                    Production Intelligence
                  </Typography>
                </Box>
                
                {(() => {
                  const actualVolume = calculateActualProductionVolume();
                  return (
                    <Stack spacing={3}>
                      {/* Main Production Volume */}
                      <Box sx={{ 
                        textAlign: 'center', 
                        p: 3, 
                        bgcolor: '#f5f5f5', 
                        borderRadius: 2, 
                        border: '1px solid #e0e0e0',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                        }
                      }}>
                        <Typography variant="h2" sx={{ fontWeight: 500, mb: 1, color: '#000000'}}>
                          {actualVolume ? 
                            `${actualVolume.volume.toFixed(3)} ${actualVolume.unit}` :
                            `${batch?.planned_quantity || 0} ${batch?.planned_unit_of_measure || 'oz'}`
                          }
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
                          Target Production Volume
                        </Typography>
                        
                        {batch?.units_to_make && (
                          <Typography variant="body2" sx={{ color: '#000000', fontWeight: 500 }}>
                            {batch.units_to_make} total units
                            {batch.pallet_quantity > 0 ? ` = ${batch.pallet_quantity} pallets` : ''}
                          </Typography>
                        )}
                      </Box>

                      {/* Collapsible Density Details */}
                      {actualVolume && actualVolume.originalUnit !== 'gal' && (
                        <Accordion 
                          expanded={accordionStates.densityConversionDetails}
                          onChange={(event, expanded) => updateAccordionState('densityConversionDetails', expanded)}
                          sx={{ 
                            border: '1px solid #e0e0e0', 
                            borderRadius: '12px !important', 
                            '&:before': { display: 'none'},
                            transition: 'all 0.3s ease',
                            '&.Mui-expanded': {
                              borderRadius: '12px !important'
                            },
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                            },
                            '& .MuiAccordion-region': {
                              borderRadius: '0 0 12px 12px !important'
                            }
                          }}
                        >
                          <AccordionSummary 
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ 
                              bgcolor: 'linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})',
                              '& .MuiAccordionSummary-content': { alignItems: 'center' }
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000'}}>
                              Density Conversion Details
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 2 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                              <strong>Original Size:</strong> {actualVolume.originalSize?.toLocaleString()} {actualVolume.originalUnit}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                              <strong>Density Used:</strong> {actualVolume.density.toFixed(3)} g/ml
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              <strong>Converted To:</strong> {actualVolume.volume.toFixed(3)} {actualVolume.unit}
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {/* Unit Breakdown Accordion */}
                      {batch?.units_to_make > 0 && (
                        <Accordion 
                          expanded={accordionStates.unitBreakdown}
                          onChange={(event, expanded) => updateAccordionState('unitBreakdown', expanded)}
                          sx={{ 
                            mt: 2,
                            border: '1px solid #e0e0e0', 
                            borderRadius: '12px !important', 
                            '&:before': { display: 'none'},
                            transition: 'all 0.3s ease',
                            '&.Mui-expanded': {
                              borderRadius: '12px !important'
                            },
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                            },
                            '& .MuiAccordion-region': {
                              borderRadius: '0 0 12px 12px !important'
                            }
                          }}
                        >
                          <AccordionSummary 
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ 
                              bgcolor: 'linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})',
                              '& .MuiAccordionSummary-content': { alignItems: 'center' }
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000'}}>
                              Unit Breakdown Details
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 2 }}>
                            {(() => {
                              const cfg = unitConfigurations.find(c => c.id === batch.unit_configuration_id) || selectedUnitConfig;
                              const unitsPerCase = cfg?.enable_cases ? cfg.units_per_case : null;
                              const casesPerPallet = cfg?.enable_pallets ? cfg.cases_per_pallet : null;
                              const palletName = cfg?.pallet_unit_name || 'pallets';
                              const caseName = cfg?.case_unit_name || 'cases';
                              const unitName = cfg?.base_unit_name || 'units';

                              const fullPallets = Math.floor(batch.pallet_quantity || 0);
                              const totalCasesFromPallets = cfg?.enable_pallets && cfg?.enable_cases ? fullPallets * casesPerPallet : 0;
                              const totalCasesAll = totalCasesFromPallets + (batch.case_quantity || 0);
                              const totalUnitsAll = (batch.units_to_make || 0);

                              const breakdown = [];

                              if (batch.pallet_quantity > 0) {
                                breakdown.push(
                                  <Box key="pallets" sx={{ mb: 1.5 }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                      <strong>{fullPallets} {palletName}</strong>
                                      {casesPerPallet ? ` × ${casesPerPallet} ${caseName}/pallet = ` : ' = '}
                                      <strong>{totalCasesFromPallets > 0 ? `${totalCasesFromPallets} ${caseName}` : '—'}</strong>
                                    </Typography>
                                    {unitsPerCase && totalCasesFromPallets > 0 && (
                                      <Typography variant="body2" sx={{ color: 'text.secondary', pl: 1 }}>
                                        {totalCasesFromPallets} {caseName} × {unitsPerCase} {unitName}/{caseName.replace(/s$/, '')} = <strong>{(totalCasesFromPallets * unitsPerCase).toLocaleString()} {unitName}</strong>
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              }

                              if (batch.case_quantity > 0) {
                                breakdown.push(
                                  <Box key="cases" sx={{ mb: 1.5 }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                      <strong>+ {batch.case_quantity} loose {caseName}</strong>
                                      {unitsPerCase ? ` × ${unitsPerCase} ${unitName}/${caseName.replace(/s$/, '')} = ` : ' = '}
                                      {unitsPerCase ? <strong>{(batch.case_quantity * unitsPerCase).toLocaleString()} {unitName}</strong> : '—'}
                                    </Typography>
                                  </Box>
                                );
                              }

                              if (batch.unit_quantity > 0) {
                                breakdown.push(
                                  <Typography key="units" variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                                    <strong>+ {batch.unit_quantity} loose {unitName}</strong>
                                  </Typography>
                                );
                              }

                              if (totalUnitsAll > 0) {
                                breakdown.push(
                                  <Box key="total" sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e0e0e0' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                      Total: {totalCasesAll > 0 ? `${totalCasesAll.toLocaleString()} ${caseName} · ` : ''}{totalUnitsAll.toLocaleString()} {unitName}
                                    </Typography>
                                  </Box>
                                );
                              }

                              return breakdown.length > 0 ? breakdown : (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  No breakdown available
                                </Typography>
                              );
                            })()}
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </Stack>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Recipe Scaling Card */}
            {selectedFormulation && (
              <Card key={`recipe-${editData?.variant_distribution?.length || 0}-${editData?.planned_quantity || 0}`} sx={{ 
                borderRadius: 3,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: `white`,
                backdropFilter: 'blur(20px)',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      backdropFilter: 'blur(10px)'
                    }}>
                      <SpeedIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2 }}>
                      Recipe Multiplier
                    </Typography>
                  </Box>
                  
                  {(() => {
                    const formulationSize = selectedFormulation.target_batch_size || 100;
                    const formulationUnit = selectedFormulation.unit_of_measure || 'gal';
                    const currentData = editDialogOpen ? editData : batch;
                    
                    // Calculate target size from variant distribution if available
                    let targetSize = 0;
                    let targetUnit = 'oz';
                    
                    if (editDialogOpen && currentData?.variant_distribution && currentData.variant_distribution.length > 0) {
                      // Calculate from variant distribution
                      currentData.variant_distribution.forEach(variant => {
                        const productVariant = productVariants.find(v => v.id === variant.variant_id);
                        if (productVariant) {
                          targetSize += variant.quantity * productVariant.volume_weight;
                          targetUnit = productVariant.volume_unit;
                        }
                      });
                    } else {
                      // Fallback to planned_quantity
                      targetSize = currentData?.planned_quantity || 0;
                      targetUnit = currentData?.planned_unit_of_measure || 'oz';
                    }
                    
                    // Calculate real scaling factor (preserve existing logic)
                    let scalingFactor = 1;
                    try {
                      if (targetUnit !== formulationUnit) {
                        const targetUnitType = getUnitType(targetUnit);
                        const formulationUnitType = getUnitType(formulationUnit);
                        
                        if (targetUnitType !== formulationUnitType) {
                          let calculatedDensity = 1.0;
                          if (formulationIngredients.length > 0) {
                            let totalWeightedDensity = 0;
                            let totalPercentage = 0;
                            
                            formulationIngredients.forEach(ingredient => {
                              const rawMaterial = ingredient.raw_materials;
                              const percentage = ingredient.percentage || 0;
                              const materialDensity = rawMaterial?.density_g_ml;
                              
                              if (materialDensity && materialDensity > 0 && percentage > 0) {
                                totalWeightedDensity += (materialDensity * percentage);
                                totalPercentage += percentage;
                              }
                            });
                            
                            if (totalPercentage > 0) {
                              calculatedDensity = totalWeightedDensity / totalPercentage;
                            }
                          }
                          
                          if (formulationUnitType === 'volume' && targetUnitType === 'weight') {
                            const formulationInTargetUnits = convertUnits(formulationSize, formulationUnit, targetUnit, calculatedDensity);
                            scalingFactor = targetSize / formulationInTargetUnits;
                          } else if (formulationUnitType === 'weight' && targetUnitType === 'volume') {
                            const formulationInTargetUnits = convertUnits(formulationSize, formulationUnit, targetUnit, calculatedDensity);
                            scalingFactor = targetSize / formulationInTargetUnits;
                          }
                        } else {
                          const targetInFormulationUnits = convertUnits(targetSize, targetUnit, formulationUnit);
                          scalingFactor = targetInFormulationUnits / formulationSize;
                        }
                      } else {
                        scalingFactor = targetSize / formulationSize;
                      }
                    } catch (error) {
                      scalingFactor = targetSize / formulationSize;
                    }
                    
                    return (
                      <Box>
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                          <Typography variant="h2" sx={{ fontWeight: 500, color: '#000000', mb: 1 }}>
                            {scalingFactor.toFixed(3)}×
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Multiply every ingredient by this factor
                          </Typography>
                        </Box>
                        
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <Paper sx={{ 
                              p: 2, 
                              textAlign: 'center', 
                              border: '1px solid #000000', 
                              bgcolor: '#f5f5f5',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                              }
                            }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                RECIPE BASE
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                                {formulationSize} {formulationUnit}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6}>
                            <Paper sx={{ 
                              p: 2, 
                              textAlign: 'center', 
                              bgcolor: '#000000', 
                              border: '1px solid #000000',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)'
                              }
                            }}>
                              <Typography variant="caption" sx={{ fontWeight: 500, color: 'white' }}>
                                TARGET BATCH
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'white' }}>
                                {targetSize?.toLocaleString()} {targetUnit}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                        
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center', border: '1px solid #e0e0e0' }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            Example: <strong>50 lbs cocoa butter</strong> × {scalingFactor.toFixed(3)} = <strong>{(50 * scalingFactor).toFixed(3)} lbs</strong>
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

                        {/* Batch Analysis Card */}
            {batch && selectedFormulation && batchAnalysis && (
              <Card key={`analysis-${editData?.variant_distribution?.length || 0}-${editData?.planned_quantity || 0}`} sx={{ 
                borderRadius: 2,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: `white`,
                backdropFilter: 'blur(20px)',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      backdropFilter: 'blur(10px)'
                    }}>
                      <InventoryIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                    </Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      ml: 2
                    }}>
                      Inventory Analysis
                    </Typography>
                  </Box>

                  {/* Check for expiring materials warning */}
                  {(() => {
                    const expiringMaterials = [];
                    if (rawMaterialsInventoryItems && rawMaterialsInventoryItems.length > 0) {
                      rawMaterialsInventoryItems.forEach(inventory => {
                        if (inventory.expiry_date) {
                          const daysToExpiry = (new Date(inventory.expiry_date) - new Date()) / (1000 * 60 * 60 * 24);
                          if (daysToExpiry <= 30 && daysToExpiry >= 0) {
                            expiringMaterials.push({
                              name: inventory.raw_material?.name || 'Unknown Material',
                              lot_number: inventory.lot_number,
                              batch_number: inventory.batch_number,
                              expiryDate: inventory.expiry_date,
                              daysToExpiry: Math.ceil(daysToExpiry)
                            });
                          }
                        }
                      });
                    }
                    
                    if (expiringMaterials.length > 0) {
                      return (
                        <Alert 
                          severity="warning" 
                          sx={{ 
                            mb: 2,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                            bgcolor: '#fff3e0',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                            }
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, color: '#000000' }}>
                            Raw Materials Expiring Soon
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#000000', mb: 1 }}>
                            {expiringMaterials.length} lot{expiringMaterials.length === 1 ? '' : 's'} will expire within 30 days:
                          </Typography>
                                    <Box sx={{ ml: 2 }}>
                                      {(showAllEditDialogExpiringMaterials ? expiringMaterials : expiringMaterials.slice(0, 3)).map((material, index) => (
                                        <Typography key={index} variant="caption" sx={{ display: 'block', color: '#000000' }}>
                                          • {material.name} {material.lot_number ? `(Lot: ${material.lot_number})` : material.batch_number ? `(Batch: ${material.batch_number})` : ''}: Expires in {material.daysToExpiry} day{material.daysToExpiry === 1 ? '' : 's'}
                                        </Typography>
                                      ))}
                                      {expiringMaterials.length > 3 && !showAllEditDialogExpiringMaterials && (
                                        <Typography 
                                          variant="caption" 
                                          color="primary" 
                                          sx={{ 
                                            cursor: 'pointer', 
                                            textDecoration: 'underline',
                                            fontWeight: 500,
                                            '&:hover': { color: 'primary.dark' }
                                          }}
                                          onClick={() => setShowAllEditDialogExpiringMaterials(true)}
                                        >
                                          ... and {expiringMaterials.length - 3} more lots (click to show all)
                                        </Typography>
                                      )}
                                      {showAllEditDialogExpiringMaterials && expiringMaterials.length > 3 && (
                                        <Typography 
                                          variant="caption" 
                                          color="primary" 
                                          sx={{ 
                                            cursor: 'pointer', 
                                            textDecoration: 'underline',
                                            fontWeight: 500,
                                            '&:hover': { color: 'primary.dark' }
                                          }}
                                          onClick={() => setShowAllEditDialogExpiringMaterials(false)}
                                        >
                                          (click to show less)
                                        </Typography>
                                      )}
                                    </Box>
                        </Alert>
                      );
                    }
                    return null;
                  })()}
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 500, 
                      mb: 1,
                      color: theme.palette.text.primary
                    }}>
                      {batchAnalysis.canMakeBatch ? 'Batch Can Be Made' :
                      batchAnalysis.noInventoryAtLocation ? 'No Materials at Location' :
                      'Insufficient Materials'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Maximum possible batches:
                      </Typography>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 500,
                        color: theme.palette.text.primary
                      }}>
                        {batchAnalysis.maxBatches === 999 ? '999+' : batchAnalysis.maxBatches}
                      </Typography>
                    </Box>
                    
                    {/* Visual Progress Bar for Materials */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        MATERIAL AVAILABILITY
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={batchAnalysis.canMakeBatch ? 100 : (batchAnalysis.maxBatches / 1) * 100}
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          bgcolor: alpha(theme.palette.grey[300], 0.3),
                          '& .MuiLinearProgress-bar': {
                            background: `linear-gradient( ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            borderRadius: 4
                          }
                        }}
                      />
                    </Box>
                  </Box>
                  
                  {batchAnalysis.shortages.length > 0 && (
                                        <Alert 
                      severity="error" 
                      sx={{ 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`,
                        border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.1)}`,
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.error.main, 0.15)}`,
                          border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.primary }}>
                        {batchAnalysis.shortages.length} Material Shortage{batchAnalysis.shortages.length > 1 ? 's' : ''}
                      </Typography>
                      {(showAllShortages ? batchAnalysis.shortages : batchAnalysis.shortages.slice(0, 3)).map((shortage, index) => (
                        <Typography key={index} variant="caption" sx={{ display: 'block', mb: 0.5, color: theme.palette.text.primary, fontWeight: 500 }}>
                          • {shortage.name}: Need {shortage.shortage.toFixed(3)} {shortage.unit} more
                        </Typography>
                      ))}
                      {batchAnalysis.shortages.length > 3 && !showAllShortages && (
                        <Typography 
                          variant="caption" 
                          color="primary" 
                          sx={{ 
                            fontWeight: 500, 
                            cursor: 'pointer', 
                            textDecoration: 'underline',
                            '&:hover': { color: 'primary.dark' }
                          }}
                          onClick={() => setShowAllShortages(true)}
                        >
                          ... and {batchAnalysis.shortages.length - 3} more (click to show all)
                        </Typography>
                      )}
                      {showAllShortages && batchAnalysis.shortages.length > 3 && (
                        <Typography 
                          variant="caption" 
                          color="primary" 
                          sx={{ 
                            fontWeight: 500, 
                            cursor: 'pointer', 
                            textDecoration: 'underline',
                            '&:hover': { color: 'primary.dark' }
                          }}
                          onClick={() => setShowAllShortages(false)}
                        >
                          (click to show less)
                        </Typography>
                      )}
                    </Alert>
                  )}
                  
                  {/* Quick Ingredients Summary */}
                  <Accordion
                    expanded={accordionStates.ingredientAvailability}
                    onChange={(event, expanded) => updateAccordionState('ingredientAvailability', expanded)}
                    elevation={0}
                    sx={{
                      mt: 2,
                      mb: 2,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(20px)',
                      overflow: 'hidden',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      '&:before': {
                        display: 'none'
                      },
                      '&.Mui-expanded': {
                        margin: '16px 0 16px 0'
                      },
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                      sx={{
                        borderRadius: '8px 8px 0 0',
                        background: 'white',
                        backdropFilter: 'blur(10px)',
                        border: 'none',
                        minHeight: 56,
                        '&.Mui-expanded': {
                          minHeight: 56,
                          borderRadius: '8px 8px 0 0'
                        },
                        '& .MuiAccordionSummary-content': {
                          margin: '12px 0',
                          alignItems: 'center',
                          '&.Mui-expanded': {
                            margin: '12px 0'
                          }
                        }
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                        Ingredient Availability ({batchAnalysis.ingredients.length} items)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 3 }}>
                      <Stack spacing={1}>
                        {(showAllIngredients ? batchAnalysis.ingredients : batchAnalysis.ingredients.slice(0, 5)).map((ingredient, index) => (
                          <Box key={index} sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2,
                            background: ingredient.hasInventory ?
                              (ingredient.canMake > 0 ?
                                `linear-gradient(${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.04)})` :
                                `linear-gradient(${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`) :
                              `linear-gradient(${alpha(theme.palette.warning.main, 0.08)}, ${alpha(theme.palette.warning.main, 0.04)})`,
                            borderRadius: 2,
                            border: `1px solid ${ingredient.hasInventory ?
                              (ingredient.canMake > 0 ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.error.main, 0.2)) :
                              alpha(theme.palette.warning.main, 0.2)}`,
                            backdropFilter: 'blur(10px)',
                            boxShadow: `0 2px 8px ${ingredient.hasInventory ?
                              (ingredient.canMake > 0 ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1)) :
                              alpha(theme.palette.warning.main, 0.1)}`,
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 16px ${ingredient.hasInventory ?
                                (ingredient.canMake > 0 ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.error.main, 0.15)) :
                                alpha(theme.palette.warning.main, 0.15)}`,
                              border: `1px solid ${ingredient.hasInventory ?
                                (ingredient.canMake > 0 ? alpha(theme.palette.success.main, 0.4) : alpha(theme.palette.error.main, 0.4)) :
                                alpha(theme.palette.warning.main, 0.4)}`
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary, fontSize: '0.8rem' }}>
                                {ingredient.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Need: {ingredient.required.toFixed(3)} {ingredient.requiredUnit}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="body2" sx={{
                                fontWeight: 500,
                                color: ingredient.hasInventory ? (ingredient.canMake > 0 ? theme.palette.success.main : theme.palette.error.main) : theme.palette.warning.main,
                                fontSize: '0.8rem'
                              }}>
                                {ingredient.hasInventory ? (
                                  ingredient.canMake > 0 ?
                                    `✓ ${ingredient.available.toFixed(3)} ${ingredient.requiredUnit}` :
                                    `✗ ${ingredient.available.toFixed(3)} ${ingredient.requiredUnit}`
                                ) : (
                                  '⚠ No stock'
                                )}
                              </Typography>
                              {ingredient.hasInventory && ingredient.canMake > 0 && (
                                <Typography variant="caption" sx={{ color: theme.palette.success.main, fontWeight: 500 }}>
                                  {ingredient.canMake === 999 ? '999+' : ingredient.canMake} batches possible
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                        {batchAnalysis.ingredients.length > 5 && !showAllIngredients && (
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{
                              textAlign: 'center',
                              mt: 1,
                              cursor: 'pointer',
                              fontWeight: 500,
                              textDecoration: 'underline',
                              '&:hover': { color: 'primary.dark' }
                            }}
                            onClick={() => setShowAllIngredients(true)}
                          >
                            ... and {batchAnalysis.ingredients.length - 5} more ingredients (click to show all)
                          </Typography>
                        )}
                        {showAllIngredients && batchAnalysis.ingredients.length > 5 && (
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{
                              textAlign: 'center',
                              mt: 1,
                              cursor: 'pointer',
                              fontWeight: 500,
                              textDecoration: 'underline',
                              '&:hover': { color: 'primary.dark' }
                            }}
                            onClick={() => setShowAllIngredients(false)}
                          >
                            (click to show less)
                          </Typography>
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                                    <Box sx={{ 
                    mt: 2,
                    p: 3, 
                    background: `linear-gradient(${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.04)})`,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    backdropFilter: 'blur(10px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.1)}`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.15)}`,
                      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                      Recommendation:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {batchAnalysis.canMakeBatch ? (
                        batchAnalysis.maxBatches > 1 ? 
                          `Perfect! You can make ${batchAnalysis.maxBatches === 999 ? 'many' : batchAnalysis.maxBatches} batches. Consider bulk production.` :
                          'Sufficient materials for this batch. Consider ordering more for future runs.'
                      ) : batchAnalysis.noInventoryAtLocation ? (
                        `Transfer materials to ${getLocationLabel(batch.production_location)} or choose a different location.`
                      ) : (
                        `Order the missing materials above to proceed with production.`
                      )}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}

          </Stack>
        </Grid>

        {/* PANEL C: Batch Status */}
        <Grid item xs={12} lg={3}>
          <Card sx={{ 
            height: 'fit-content',
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: `white`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backdropFilter: 'blur(10px)'
                }}>
                  <TimelineIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2 }}>
                  Batch Status
                </Typography>
              </Box>
              
              <Stack spacing={3}>
                {/* Current Status Display */}
                <Box sx={{ 
                  p: 3,
                  border: batch?.status === 'completed' 
                    ? `1px solid ${alpha(theme.palette.grey[400], 0.4)}`
                    : `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: 2,
                  textAlign: 'center',
                  background: batch?.status === 'completed' 
                    ? alpha(theme.palette.grey[100], 0.7)
                    : `white`,
                  backdropFilter: 'blur(10px)',
                  boxShadow: batch?.status === 'completed'
                    ? `0 2px 8px ${alpha(theme.palette.grey[400], 0.1)}`
                    : `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                  opacity: batch?.status === 'completed' ? 0.8 : 1,
                  '&:hover': batch?.status !== 'completed' ? {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  } : {},
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 500, 
                    color: batch?.status === 'completed' 
                      ? alpha(theme.palette.text.secondary, 0.7)
                      : theme.palette.text.primary, 
                    mb: 1 
                  }}>
                    {formatStatus(batch?.status)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    fontWeight: 500,
                    opacity: batch?.status === 'completed' ? 0.7 : 1
                  }}>
                    Current batch status
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary, mt: 1 }}>
                    Priority: {batch?.priority?.toUpperCase() || 'NORMAL'}
                  </Typography>
                </Box>

                                {/* Collapsible Production Details */}
                {(batch?.production_line || batch?.shift_supervisor) && (
                  <Accordion 
                    expanded={accordionStates.productionDetails}
                    onChange={(event, expanded) => updateAccordionState('productionDetails', expanded)}
                    elevation={0}
                    sx={{
                      mt: 3,
                      mb: 3,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(20px)',
                      overflow: 'hidden',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      '&:before': {
                        display: 'none'
                      },
                      '&.Mui-expanded': {
                        margin: '24px 0 24px 0'
                      },
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      borderRadius: '8px 8px 0 0',
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      border: 'none',
                      minHeight: 56,
                      '&.Mui-expanded': {
                        minHeight: 56,
                        borderRadius: '8px 8px 0 0'
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                        alignItems: 'center',
                        '&.Mui-expanded': {
                          margin: '12px 0'
                        }
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                      Production Details
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3 }}>
                      <Stack spacing={2}>
                        {batch?.production_line && (
                                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Tank #:
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                {batch.production_line}
                              </Typography>
                            </Box>
                          )}
                          
                          {batch?.shift_supervisor && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Supervisor:
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                {batch.shift_supervisor}
                              </Typography>
                            </Box>
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}

                                {/* Collapsible Production Notes */}
                {batch?.production_notes && (
                  <Accordion 
                    expanded={accordionStates.productionNotes}
                    onChange={(event, expanded) => updateAccordionState('productionNotes', expanded)}
                    elevation={0}
                    sx={{
                      mt: 3,
                      mb: 3,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(20px)',
                      overflow: 'hidden',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      '&:before': {
                        display: 'none'
                      },
                      '&.Mui-expanded': {
                        margin: '24px 0 24px 0'
                      },
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      borderRadius: '8px 8px 0 0',
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      border: 'none',
                      minHeight: 56,
                      '&.Mui-expanded': {
                        minHeight: 56,
                        borderRadius: '8px 8px 0 0'
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                        alignItems: 'center',
                        '&.Mui-expanded': {
                          margin: '12px 0'
                        }
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                      Production Notes
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {batch.production_notes}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
                )}
                
                {/* Timeline */}
                <Accordion 
                  expanded={accordionStates.timeline}
                  onChange={(event, expanded) => updateAccordionState('timeline', expanded)}
                  elevation={0}
                  sx={{
                    mt: 3,
                    mb: 3,
                    borderRadius: '12px !important',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(20px)',
                    overflow: 'hidden',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      margin: '24px 0 24px 0',
                      borderRadius: '12px !important'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiAccordion-region': {
                      borderRadius: '0 0 12px 12px !important'
                    }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      border: 'none',
                      minHeight: 56,
                      '&.Mui-expanded': {
                        minHeight: 56
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                        alignItems: 'center',
                        '&.Mui-expanded': {
                          margin: '12px 0'
                        }
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                      Timeline
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3 }}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        <strong>Created:</strong> {formatDateForDisplay(batch?.created_at?.split('T')[0])}
                      </Typography>
                      {batch?.updated_at && batch?.updated_at !== batch?.created_at && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          <strong>Updated:</strong> {formatDateForDisplay(batch?.updated_at?.split('T')[0])}
                        </Typography>
                      )}
                      {batch?.planned_start_date && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          <strong>Planned Start:</strong> {formatDateForDisplay(batch?.planned_start_date)}
                          {batch?.planned_start_time && ` at ${batchTimeFormat === '24hour' ? batch.planned_start_time : dayjs(`2000-01-01 ${batch.planned_start_time}`).format('h:mm A')}`}
                        </Typography>
                      )}
                      {batch?.planned_completion_date && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          <strong>Planned Completion:</strong> {formatDateForDisplay(batch?.planned_completion_date)}
                          {batch?.planned_completion_time && ` at ${batchTimeFormat === '24hour' ? batch.planned_completion_time : dayjs(`2000-01-01 ${batch.planned_completion_time}`).format('h:mm A')}`}
                        </Typography>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={(event, reason) => { if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') handleCancel(); }}
        disableEscapeKeyDown
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: 'white',
              color: '#000000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FactoryIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 0.5 }}>
                {isCreatingNew ? 'Create New Production Batch' : 'Edit Production Batch'}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                Configure batch parameters and production details
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        {validationError && (
          <Box sx={{ px: 3, pt: 3 }}>
            <Alert 
              severity="error" 
              sx={{ 
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                fontWeight: 500,
                '& .MuiAlert-message': {
                  whiteSpace: 'pre-line' // Allow line breaks in error messages
                }
              }}
            >
              {validationError}
            </Alert>
          </Box>
        )}
        <DialogContent sx={{ backgroundColor: '#f8fafc', p: 4, mt: 2, maxHeight: '80vh', overflow: 'auto'}}>
          {/* Calendar Autofill Widget */}
          {isCreatingNew && getTodaysCalendarBatches().length > 0 && activeStep === 0 && (
            <Box sx={{ mb: 3 }}>
              <Card sx={{ 
                p: 3, 
                border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                borderRadius: 3, 
                backgroundColor: alpha(theme.palette.success.main, 0.05),
                boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.08)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.12)}`,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.4)}`
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CalendarIcon sx={{ color: theme.palette.success.main, fontSize: 24, mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.success.main }}>
                    Calendar Batches for {selectedDate ? selectedDate.toLocaleDateString() : 'Today'}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 3 }}>
                  Found {getTodaysCalendarBatches().length} planned batch{getTodaysCalendarBatches().length === 1 ? '' : 'es'} for this date. Click to autofill data:
                </Typography>
                
                <Grid container spacing={2}>
                  {getTodaysCalendarBatches().map((calendarBatch, index) => (
                    <Grid item xs={12} sm={6} key={calendarBatch.id}>
                      <Card sx={{ 
                        p: 2, 
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        borderRadius: 2,
                        background: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`,
                          borderColor: theme.palette.primary.main
                        }
                      }}
                      onClick={() => handleAutofillFromCalendar(calendarBatch)}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                          {calendarBatch.batch_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {calendarBatch.planned_start_time ? 
                            `${calendarBatch.planned_start_time} - ${calendarBatch.planned_completion_time || ''}` : 
                            'No time set'}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Chip 
                            label={calendarBatch.status?.toUpperCase() || 'DRAFT'}
                            size="small"
                            sx={{ 
                              fontWeight: 500,
                              fontSize: '0.7rem'
                            }}
                          />
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Box>
          )}
          
          {/* Required Fields Notice */}
          {activeStep === 0 && (
            <Box sx={{ mb: 3 }}>
              <Alert 
                severity="info" 
                sx={{ 
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.info.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                  '& .MuiAlert-icon': {
                    color: theme.palette.info.main
                  }
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Fields marked with an asterisk (*) are required. Please fill in all required fields before saving.
                </Typography>
              </Alert>
            </Box>
          )}
          
          <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 4 }}>
            {/* Step 1: Basic Information */}
            <Step>
              <StepLabel 
                onClick={() => handleStep(0)}
                sx={{ cursor: 'pointer' }}
              >
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Basic Information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Batch identifiers and status configuration
                </Typography>
              </StepLabel>
              <StepContent>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Batch Number"
                value={editData.batch_number}
                onChange={(e) => setEditData({ ...editData, batch_number: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Internal Code (Optional)"
                value={editData.internal_code}
                onChange={(e) => setEditData({ ...editData, internal_code: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  '& fieldset': {
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    borderWidth: 1
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main
                  }
                }
              }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editData.status}
                  onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                  label="Status"
                  disabled={editData.status === 'completed'}
                  sx={{
                    ...(editData.status === 'completed' && {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.grey[400], 0.5)
                      },
                      '& .MuiInputLabel-root': {
                        color: alpha(theme.palette.text.secondary, 0.6)
                      },
                      '& .MuiSelect-select': {
                        color: alpha(theme.palette.text.secondary, 0.6)
                      }
                    })
                  }}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="pending_approval">Pending Approval</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="in_production">In Production</MenuItem>
                  <MenuItem value="quality_testing">Quality Testing</MenuItem>
                  <MenuItem 
                    value="completed" 
                    disabled
                    sx={{
                      color: alpha(theme.palette.text.disabled, 0.5),
                      '&.Mui-disabled': {
                        opacity: 0.6
                      }
                    }}
                  >
                    Completed
                  </MenuItem>
                  <MenuItem value="on_hold">On Hold</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
                </Grid>
              </StepContent>
            </Step>

            {/* Step 2: Production Planning */}
            <Step>
              <StepLabel 
                onClick={() => handleStep(1)}
                sx={{ cursor: 'pointer' }}
              >
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Production Planning
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Product, formulation, and production location
                </Typography>
              </StepLabel>
              <StepContent>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backdropFilter: 'blur(10px)',
                  color: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2
                }}>
                  <LocalDrinkIcon sx={{ fontSize: 24 }} />
                </Box>
                                 <Box>
                   <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 0.5 }}>
                     Production Planning
                   </Typography>
                   <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                     Select product and configure manufacturing details
                   </Typography>
                 </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleOpenProductSelection}
                sx={{
                  height: 56,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  justifyContent: 'flex-start',
                  px: 3,
                  py: 2.5,
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <InventoryIcon sx={{ mr: 2, color: 'text.secondary' }} />
                  <Box sx={{ textAlign: 'left', flex: 1 }}>
                    {editData.product_id ? (
                      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                        {products.find(p => p.id === editData.product_id)?.name || 'Unknown Product'}
                      </Typography>
                    ) : (
                      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                        Select Product to Make
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Button>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleOpenFormulationSelection}
                sx={{
                  height: 56,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  justifyContent: 'flex-start',
                  px: 3,
                  py: 2.5,
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <ScienceIcon sx={{ mr: 2, color: 'text.secondary' }} />
                  <Box sx={{ textAlign: 'left', flex: 1 }}>
                    {editData.formulation_id ? (
                      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                        {formulations.find(f => f.id === editData.formulation_id)?.name || 'Unknown Formulation'}
                      </Typography>
                    ) : (
                      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                        Select Formulation
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Button>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleOpenLocationSelection}
                sx={{
                  height: 56,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  justifyContent: 'flex-start',
                  px: 3,
                  py: 2.5,
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <BusinessIcon sx={{ mr: 2, color: 'text.secondary' }} />
                  <Box sx={{ textAlign: 'left', flex: 1 }}>
                    {editData.production_location ? (
                      <>
                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          {locations.find(l => l.id === editData.production_location)?.name || 'Unknown Location'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(() => {
                            const location = locations.find(l => l.id === editData.production_location);
                            return location ? `${location.facility_type} • ${location.city}, ${location.state}` : 'No details';
                          })()}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                        Select Production Location
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Button>
            </Grid>
                </Grid>
              </StepContent>
            </Step>

            {/* Step 3: Packaging & Quantities */}
            <Step>
              <StepLabel
                onClick={() => handleStep(2)}
                sx={{ cursor: 'pointer' }}
              >
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Packaging & Quantities
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unit configuration and production quantities
                </Typography>
              </StepLabel>
              <StepContent>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  {editData.product_id ? (
                    <>
                      {/* Unit Configuration Section */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        color: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2
                      }}>
                        <SettingsIcon sx={{ fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 0.5 }}>
                          Unit Configuration
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          Select packaging structure first
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setUnitConfigDialogOpen(true)}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1.2,
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        bgcolor: 'primary.main',
                        boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                        '&:hover': {
                          bgcolor: 'primary.dark',
                          transform: 'translateY(-1px)',
                          boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                        }
                      }}
                    >
                      Add Configuration
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  {unitConfigurations.length === 0 ? (
                    <Box sx={{
                      textAlign: 'center',
                      py: 6,
                      px: 4,
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.grey[50], 0.5)
                    }}>
                      <SettingsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                        No Unit Configurations Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 300, mx: 'auto' }}>
                        Create unit configurations to define packaging structures.
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      {selectedUnitConfig ? (
                        <Card sx={{ 
                          borderRadius: 3, 
                          border: `2px solid ${theme.palette.primary.main}`,
                          background: alpha(theme.palette.primary.main, 0.05),
                          p: 3,
                          mb: 2
                        }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem', mb: 1 }}>
                                {selectedUnitConfig.name}
                              </Typography>
                              
                              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontSize: '0.85rem' }}>
                                {selectedUnitConfig.enable_cases && !selectedUnitConfig.enable_pallets && `${selectedUnitConfig.base_unit_name} → ${selectedUnitConfig.case_unit_name}`}
                                {selectedUnitConfig.enable_cases && selectedUnitConfig.enable_pallets && `${selectedUnitConfig.base_unit_name} → ${selectedUnitConfig.case_unit_name} → ${selectedUnitConfig.pallet_unit_name}`}
                                {!selectedUnitConfig.enable_cases && !selectedUnitConfig.enable_pallets && `${selectedUnitConfig.base_unit_name} only`}
                              </Typography>

                              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                {selectedUnitConfig.enable_cases && (
                                  <Chip 
                                    label="Cases" 
                                    size="small" 
                                    sx={{
                                      bgcolor: alpha(theme.palette.info.main, 0.1),
                                      color: 'info.main',
                                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                      fontWeight: 500,
                                      fontSize: '0.75rem',
                                      height: 24
                                    }}
                                  />
                                )}
                                {selectedUnitConfig.enable_pallets && (
                                  <Chip 
                                    label="Pallets" 
                                    size="small" 
                                    sx={{
                                      bgcolor: alpha(theme.palette.success.main, 0.1),
                                      color: 'success.main',
                                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                      fontWeight: 500,
                                      fontSize: '0.75rem',
                                      height: 24
                                    }}
                                  />
                                )}
                              </Box>

                              <Grid container spacing={2}>
                                <Grid item xs={4}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Units/Case
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                    {selectedUnitConfig.enable_cases ? selectedUnitConfig.units_per_case : 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Cases/Pallet
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                    {selectedUnitConfig.enable_pallets ? selectedUnitConfig.cases_per_pallet : 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Total/Pallet
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                                    {selectedUnitConfig.enable_pallets && selectedUnitConfig.enable_cases
                                      ? (selectedUnitConfig.units_per_case * selectedUnitConfig.cases_per_pallet).toLocaleString()
                                      : selectedUnitConfig.enable_cases
                                      ? `${selectedUnitConfig.units_per_case}/case`
                                      : '1'
                                    }
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={handleOpenUnitConfigSelection}
                              sx={{
                                ml: 2,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.85rem'
                              }}
                            >
                              Change
                            </Button>
                          </Box>
                        </Card>
                      ) : (
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={handleOpenUnitConfigSelection}
                          sx={{
                            py: 3,
                            borderRadius: 3,
                            border: `2px dashed ${alpha(theme.palette.grey[400], 0.8)}`,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '1rem',
                            color: 'text.secondary',
                            '&:hover': {
                              border: `2px solid ${theme.palette.primary.main}`,
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                              color: 'primary.main'
                            }
                          }}
                        >
                          <SettingsIcon sx={{ mr: 1 }} />
                          Select Unit Configuration
                        </Button>
                      )}
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      backdropFilter: 'blur(10px)',
                      color: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}>
                      <ScaleIcon sx={{ fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 0.5 }}>
                        Variant Distribution
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Specify how many units of each size you want to make from this batch
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {selectedUnitConfig && productVariants.length > 0 && (editData.product_variant_id || (editData.variant_distribution && editData.variant_distribution.length > 0)) ? (
                  productVariants.filter(variant => {
                    // If single variant mode, filter by product_variant_id
                    if (editData.product_variant_id) {
                      return variant.id === editData.product_variant_id;
                    }
                    // If multi-variant mode, show all variants that are in the distribution
                    return editData.variant_distribution && editData.variant_distribution.some(d => d.variant_id === variant.id);
                  })
                  .sort((a, b) => {
                    const volumeA = parseFloat(a.volume_weight) || 0;
                    const volumeB = parseFloat(b.volume_weight) || 0;
                    return volumeB - volumeA; // Descending order (largest first)
                  })
                  .map((variant, index) => (
                    <Grid item xs={12} key={variant.id}>
                      <Card sx={{ 
                        p: 4, 
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        backdropFilter: 'blur(20px)',
                        transition: 'all 0.3s ease',
                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                          borderColor: theme.palette.primary.main
                        }
                      }}>
                        <Typography variant="h5" sx={{ fontWeight: 500, color: '#000000', mb: 3 }}>
                          {variant.volume_weight}{variant.volume_unit} Production
                          {variant.sku && (
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 500, ml: 2 }}>
                              (SKU: {variant.sku})
                            </Typography>
                          )}
                        </Typography>

                        <Grid container spacing={3}>
                          {selectedUnitConfig.enable_pallets && (
                            <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label={`${(selectedUnitConfig.pallet_unit_name || 'Pallets').charAt(0).toUpperCase() + (selectedUnitConfig.pallet_unit_name || 'pallets').slice(1)} to Make`}
                              type="number"
                              value={unitInputs[variant.id]?.pallets || ''}
                              onChange={(e) => {
                                const newPallets = parseInt(e.target.value) || 0;
                                const newInputs = { ...(unitInputs[variant.id] || {}), pallets: newPallets };
                                setUnitInputs(prev => ({ ...prev, [variant.id]: newInputs }));
                                updateVariantFromUnitInputs(variant.id, newInputs);
                              }}
                              inputProps={{ min: 0, step: 1 }}
                              placeholder="0"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    backdropFilter: 'blur(10px)',
                                    '& fieldset': {
                                      borderColor: alpha(theme.palette.primary.main, 0.3),
                                      borderWidth: 1
                                    },
                                    '&:hover fieldset': {
                                      borderColor: theme.palette.primary.main
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: theme.palette.primary.main
                                    }
                                  }
                                }}
                              />
                            </Grid>
                          )}

                          {selectedUnitConfig.enable_cases && (
                            <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label={`${(selectedUnitConfig.case_unit_name || 'Cases').charAt(0).toUpperCase() + (selectedUnitConfig.case_unit_name || 'cases').slice(1)} to Make`}
                              type="number"
                              value={unitInputs[variant.id]?.cases || ''}
                              onChange={(e) => {
                                const newCases = parseInt(e.target.value) || 0;
                                const newInputs = { ...(unitInputs[variant.id] || {}), cases: newCases };
                                setUnitInputs(prev => ({ ...prev, [variant.id]: newInputs }));
                                updateVariantFromUnitInputs(variant.id, newInputs);
                              }}
                              inputProps={{ min: 0, step: 1 }}
                              placeholder="0"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    backdropFilter: 'blur(10px)',
                                    '& fieldset': {
                                      borderColor: alpha(theme.palette.primary.main, 0.3),
                                      borderWidth: 1
                                    },
                                    '&:hover fieldset': {
                                      borderColor: theme.palette.primary.main
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: theme.palette.primary.main
                                    }
                                  }
                                }}
                              />
                            </Grid>
                          )}

                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label={`${(selectedUnitConfig.base_unit_name || 'Units').charAt(0).toUpperCase() + (selectedUnitConfig.base_unit_name || 'units').slice(1)} to Make`}
                              type="number"
                              value={unitInputs[variant.id]?.units || ''}
                              onChange={(e) => {
                                const newUnits = parseInt(e.target.value) || 0;
                                const newInputs = { ...(unitInputs[variant.id] || {}), units: newUnits };
                                setUnitInputs(prev => ({ ...prev, [variant.id]: newInputs }));
                                updateVariantFromUnitInputs(variant.id, newInputs);
                              }}
                              inputProps={{ min: 0, step: 1 }}
                              placeholder="0"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                  backdropFilter: 'blur(10px)',
                                  '& fieldset': {
                                    borderColor: alpha(theme.palette.primary.main, 0.3),
                                    borderWidth: 1
                                  },
                                  '&:hover fieldset': {
                                    borderColor: theme.palette.primary.main
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: theme.palette.primary.main
                                  }
                                }
                              }}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Box sx={{ 
                              backgroundColor: '#f8fafc',
                              borderRadius: 2,
                              p: 3,
                              border: '1px solid #e0e0e0',
                              textAlign: 'center'
                            }}>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 1 }}>
                                Total: {calculateTotalUnitsFromInputs(variant.id, unitInputs[variant.id])} {selectedUnitConfig.base_unit_name || 'units'}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                = {(calculateTotalUnitsFromInputs(variant.id, unitInputs[variant.id]) * variant.volume_weight).toLocaleString()} {variant.volume_unit} production volume
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                  ))
                ) : !selectedUnitConfig ? (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 4 }}>
                      Please select a unit configuration first.
                    </Typography>
                  </Grid>
                ) : productVariants.length === 0 ? (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 4 }}>
                      No variants available for this product. Please add variants first.
                    </Typography>
                  </Grid>
                ) : null}

                <Grid item xs={12}>
                  <Card sx={{ 
                    p: 3, 
                    backgroundColor: '#e8f5e8',
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.08)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.12)}`,
                      borderColor: '#388e3c'
                    }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <TrendingUpIcon sx={{ color: '#2e7d32', fontSize: 32, mr: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                        Total Production
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 500, color: '#2e7d32', mb: 1 }}>
                      {calculateTotalProduction().totalUnits} units = {calculateTotalProduction().totalVolume.toFixed(3)} {calculateTotalProduction().productionUnit}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {editData.variant_distribution?.length > 0 && 
                        `Distribution: ${editData.variant_distribution
                          .map(d => {
                            const variant = productVariants.find(v => v.id === d.variant_id);
                            return variant ? { ...d, variant } : null;
                          })
                          .filter(Boolean)
                          .sort((a, b) => {
                            const volumeA = parseFloat(a.variant.volume_weight) || 0;
                            const volumeB = parseFloat(b.variant.volume_weight) || 0;
                            return volumeB - volumeA; // Descending order (largest first)
                          })
                          .map(d => `${d.quantity}×${d.variant.volume_weight}${d.variant.volume_unit}`)
                          .join(' + ')}`
                      }
                    </Typography>
                  </Card>
                </Grid>

                                 {calculateTotalProduction().totalVolume > 0 && (
                   <Grid item xs={12}>
                   </Grid>
                 )}

                 {/* Formulation Scaling Info */}
                 {selectedFormulation && calculateTotalProduction().totalVolume > 0 && (
                   <Grid item xs={12}>
                     <Card sx={{ 
                       p: 4, 
                       background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)}, ${alpha(theme.palette.warning.main, 0.04)})`,
                       borderRadius: 3,
                       border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                       backdropFilter: 'blur(20px)',
                       boxShadow: `0 4px 20px ${alpha(theme.palette.warning.main, 0.08)}`,
                       transition: 'all 0.3s ease',
                       '&:hover': {
                         transform: 'translateY(-2px)',
                         boxShadow: `0 8px 32px ${alpha(theme.palette.warning.main, 0.12)}`
                       }
                     }}>
                       {(() => {
                         const scaling = calculateFormulationScaling();
                         if (!scaling) return null;
                         
                         const totalProduction = calculateTotalProduction();
                         const batchesNeeded = Math.ceil(scaling.scalingFactor);
                         const isExactBatch = Math.abs(scaling.scalingFactor - Math.round(scaling.scalingFactor)) < 0.001;
                         
                         return (
                           <>
                             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                               <CalculateIcon sx={{ color: '#f57c00', fontSize: 32, mr: 2 }} />
                               <Typography variant="h5" sx={{ fontWeight: 500, color: '#f57c00' }}>
                                 {isExactBatch ? `Exactly ${Math.round(scaling.scalingFactor)} batch${Math.round(scaling.scalingFactor) === 1 ? '' : 'es'} needed` : 
                                  `Recipe Multiplier: ${scaling.scalingFactor.toFixed(3)}x`}
                               </Typography>
                             </Box>
                             
                                                            {!isExactBatch && (
                                 <Card sx={{ 
                                   p: 3, 
                                   mb: 3, 
                                   backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                   border: '1px solid rgba(245, 124, 0, 0.3)',
                                   borderRadius: 2,
                                   textAlign: 'center',
                                   transition: 'all 0.3s ease',
                                   '&:hover': {
                                     transform: 'translateY(-2px)',
                                     boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                                   }
                                 }}>
                                   <Typography variant="h6" sx={{ color: '#f57c00', mb: 2, fontWeight: 500 }}>
                                     What this means:
                                   </Typography>
                                   <Typography variant="body1" sx={{ color: 'text.primary', mb: 2, fontWeight: 500 }}>
                                     Take your base recipe and multiply <strong>every ingredient</strong> by {scaling.scalingFactor.toFixed(3)}
                                   </Typography>
                                   <Box sx={{ 
                                     p: 2, 
                                     backgroundColor: 'rgba(245, 124, 0, 0.1)', 
                                     borderRadius: 1,
                                     border: '1px dashed #f57c00',
                                     mb: 2
                                   }}>
                                     <Typography variant="body2" sx={{ color: '#f57c00', fontStyle: 'italic', fontWeight: 500 }}>
                                       Example: 50 lbs cocoa butter × {scaling.scalingFactor.toFixed(3)} = {(50 * scaling.scalingFactor).toFixed(3)} lbs
                                     </Typography>
                                   </Box>
                                   <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                                     Density-based calculation ({scaling.density.toFixed(3)} g/ml)
                                   </Typography>
                                 </Card>
                               )}
                             
                             <Grid container spacing={2} sx={{ mb: 3 }}>
                               <Grid item xs={12} md={4}>
                                 <Card sx={{ 
                                   textAlign: 'center', 
                                   p: 3, 
                                   backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                   border: '1px solid #e0e0e0',
                                   borderRadius: 2,
                                   height: 120,
                                   display: 'flex',
                                   flexDirection: 'column',
                                   justifyContent: 'center',
                                   transition: 'all 0.3s ease',
                                   '&:hover': {
                                     transform: 'translateY(-4px)',
                                     boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                     borderColor: '#333',
                                     backgroundColor: 'rgba(255, 255, 255, 1)',
                                     border: `1px solid black`
                                   }
                                 }}>
                                   <Typography variant="overline" sx={{ color: '#666', fontWeight: 500, fontSize: '0.8rem' }}>
                                     FORMULATION BASE
                                   </Typography>
                                   <Typography variant="h4" sx={{ fontWeight: 500, color: '#333', my: 1 }}>
                                     {scaling.formulationSize} {scaling.originalFormulationUnit}
                                   </Typography>
                                   <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                     Recipe batch size
                                   </Typography>
                                 </Card>
                               </Grid>
                               
                               <Grid item xs={12} md={4}>
                                 <Card sx={{ 
                                   textAlign: 'center', 
                                   p: 3, 
                                   backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                   border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                                   borderRadius: 2,
                                   height: 120,
                                   display: 'flex',
                                   flexDirection: 'column',
                                   justifyContent: 'center',
                                   transition: 'all 0.3s ease',
                                   '&:hover': {
                                     transform: 'translateY(-2px)',
                                     boxShadow: `0 4px 16px ${alpha(theme.palette.warning.main, 0.15)}`,
                                     borderColor: theme.palette.warning.main,
                                     backgroundColor: 'rgba(255, 255, 255, 1)'
                                   }
                                 }}>
                                   <Typography variant="overline" sx={{ color: '#f57c00', fontWeight: 500, fontSize: '0.8rem' }}>
                                     PRODUCTION TARGET
                                   </Typography>
                                   <Typography variant="h4" sx={{ fontWeight: 500, color: '#f57c00', my: 1 }}>
                                     {totalProduction.totalVolume.toFixed(3)} {scaling.originalProductionUnit}
                                   </Typography>
                                   <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                     {totalProduction.totalUnits} units total
                                   </Typography>
                                 </Card>
                               </Grid>

                               <Grid item xs={12} md={4}>
                                 <Card sx={{ 
                                   textAlign: 'center', 
                                   p: 3, 
                                   backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                   border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                                   borderRadius: 2,
                                   height: 120,
                                   display: 'flex',
                                   flexDirection: 'column',
                                   justifyContent: 'center',
                                   transition: 'all 0.3s ease',
                                   '&:hover': {
                                     transform: 'translateY(-2px)',
                                     boxShadow: `0 4px 16px ${alpha(theme.palette.info.main, 0.15)}`,
                                     borderColor: theme.palette.info.main,
                                     backgroundColor: 'rgba(255, 255, 255, 1)'
                                   }
                                 }}>
                                   <Typography variant="overline" sx={{ color: '#2196f3', fontWeight: 500, fontSize: '0.8rem' }}>
                                     ACTUAL VOLUME
                                   </Typography>
                                   <Typography variant="h4" sx={{ fontWeight: 500, color: '#2196f3', my: 1 }}>
                                     {(() => {
                                       const actualVolume = calculateActualProductionVolumeEdit();
                                       return actualVolume ? 
                                         `${actualVolume.volume.toFixed(3)} ${actualVolume.unit}` :
                                         `${totalProduction.totalVolume.toFixed(3)} ${totalProduction.productionUnit}`;
                                     })()}
                                   </Typography>
                                   <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                     {(() => {
                                       const actualVolume = calculateActualProductionVolumeEdit();
                                       return actualVolume && actualVolume.originalUnit !== actualVolume.unit ? 
                                         `Density: ${actualVolume.density.toFixed(3)} g/ml` :
                                         'Production volume';
                                     })()}
                                   </Typography>
                                 </Card>
                               </Grid>
                             </Grid>
                             
                             {/* Batch Analysis */}
                             <Card sx={{ 
                               p: 3, 
                               backgroundColor: '#e8f5e8',
                               borderRadius: 2,
                               border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                               textAlign: 'center',
                               boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.08)}`,
                               transition: 'all 0.3s ease',
                               '&:hover': {
                                 transform: 'translateY(-2px)',
                                 boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                               }
                             }}>
                               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                 <ScienceIcon sx={{ color: '#2e7d32', fontSize: 28, mr: 1.5 }} />
                                 <Typography variant="h6" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                                   Batch Analysis
                                 </Typography>
                               </Box>
                               <Typography variant="body1" sx={{ color: '#2e7d32', fontWeight: 500, mb: 2 }}>
                                 <strong>Scaling Factor:</strong> {scaling.scalingFactor.toFixed(3)}x
                                 {isExactBatch ? ' (Perfect match!)' : ` (${batchesNeeded} ${batchesNeeded === 1 ? 'batch' : 'batches'} recommended)`}
                               </Typography>
                               
                               {/* Variant Breakdown */}
                               {editData.variant_distribution && editData.variant_distribution.length > 0 && (
                                 <Box sx={{ 
                                   mt: 3, 
                                   pt: 3, 
                                   borderTop: '1px solid rgba(46, 125, 50, 0.2)',
                                   backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                   borderRadius: 2,
                                   p: 2,
                                   transition: 'all 0.3s ease',
                                   '&:hover': {
                                     transform: 'translateY(-2px)',
                                     boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                                   }
                                 }}>
                                   <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: '#2e7d32' }}>
                                     Product Mix:
                                   </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
                                    {editData.variant_distribution
                                      .map(d => {
                                        const variant = productVariants.find(v => v.id === d.variant_id);
                                        return variant ? { ...d, variant } : null;
                                      })
                                      .filter(Boolean)
                                      .sort((a, b) => {
                                        const volumeA = parseFloat(a.variant.volume_weight) || 0;
                                        const volumeB = parseFloat(b.variant.volume_weight) || 0;
                                        return volumeB - volumeA; // Descending order (largest first)
                                      })
                                      .map(d => (
                                        <Chip
                                          key={d.variant_id}
                                          label={`${d.quantity}× ${d.variant.volume_weight}${d.variant.volume_unit}`}
                                          sx={{ 
                                            bgcolor: '#4caf50',
                                            color: 'white',
                                            fontWeight: 500,
                                            fontSize: '0.9rem',
                                            height: 36,
                                            '&:hover': {
                                              bgcolor: '#388e3c'
                                            }
                                          }}
                                        />
                                      ))}
                                  </Box>
                                 </Box>
                               )}
                             </Card>
                           </>
                         );
                       })()}
                     </Card>
                   </Grid>
                 )}

                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4, fontStyle: 'italic' }}>
                        Please select a product in the Production Planning step first.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </StepContent>
            </Step>

            {/* Step 4: Dates & Schedule */}
            <Step>
              <StepLabel
                onClick={() => handleStep(3)}
                sx={{ cursor: 'pointer' }}
              >
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Dates & Schedule
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Set manufacturing timeline and deadlines
                </Typography>
              </StepLabel>
              <StepContent>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 3 }}>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Time Format</InputLabel>
                  <Select
                    value={batchTimeFormat}
                    onChange={(e) => updateBatchTimeFormat(e.target.value)}
                    label="Time Format"
                    sx={{
                      borderRadius: 2,
                      backgroundColor: 'white',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                          borderWidth: 1
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main
                        }
                      }
                    }}
                  >
                    <MenuItem value="12hour">12 Hour</MenuItem>
                    <MenuItem value="24hour">24 Hour</MenuItem>
                    </Select>
                  </FormControl>
                    </Box>
                  </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Manufacture Date"
                  value={editData.manufacture_date ? dayjs(editData.manufacture_date) : null}
                  onChange={(newValue) => {
                    setEditData({ 
                      ...editData, 
                      manufacture_date: newValue ? newValue.format('YYYY-MM-DD') : ''
                    });
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          '& fieldset': {
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            borderWidth: 1
                          },
                          '&:hover fieldset': {
                            borderColor: theme.palette.primary.main
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main
                          }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Expiration Date"
                  value={editData.expiration_date ? dayjs(editData.expiration_date) : null}
                  onChange={(newValue) => {
                    setEditData({ 
                      ...editData, 
                      expiration_date: newValue ? newValue.format('YYYY-MM-DD') : ''
                    });
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          '& fieldset': {
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            borderWidth: 1
                          },
                          '&:hover fieldset': {
                            borderColor: theme.palette.primary.main
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main
                          }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Planned Start Date & Time"
                  value={editData.planned_start_datetime}
                  onChange={(newValue) => {
                    setEditData({ 
                      ...editData, 
                      planned_start_datetime: newValue
                    });
                  }}
                  format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
                  ampm={batchTimeFormat !== '24hour'}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          '& fieldset': {
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            borderWidth: 1
                          },
                          '&:hover fieldset': {
                            borderColor: theme.palette.primary.main
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main
                          }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Planned Completion Date & Time"
                  value={editData.planned_completion_datetime}
                  onChange={(newValue) => {
                    setEditData({ 
                      ...editData, 
                      planned_completion_datetime: newValue
                    });
                  }}
                  format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
                  ampm={batchTimeFormat !== '24hour'}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          '& fieldset': {
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            borderWidth: 1
                          },
                          '&:hover fieldset': {
                            borderColor: theme.palette.primary.main
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main
                          }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
                </Grid>
              </StepContent>
            </Step>

            {/* Step 5: Production Details */}
            <Step>
              <StepLabel
                onClick={() => handleStep(4)}
                sx={{ cursor: 'pointer' }}
              >
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Production Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Production line, supervisor, priority, and notes
                </Typography>
              </StepLabel>
              <StepContent>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  <Grid item xs={12} sm={6}>
              <FormControl fullWidth required sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  '& fieldset': {
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    borderWidth: 1
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main
                  }
                }
              }}>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={editData.priority}
                  onChange={(e) => setEditData({ ...editData, priority: e.target.value })}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tank #"
                value={editData.production_line}
                onChange={(e) => setEditData({ ...editData, production_line: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Shift Supervisor"
                value={editData.shift_supervisor}
                onChange={(e) => setEditData({ ...editData, shift_supervisor: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Production Notes (Optional)"
                multiline
                rows={3}
                value={editData.production_notes}
                onChange={(e) => setEditData({ ...editData, production_notes: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>

            {/* Batch Analysis */}
            {editData.formulation_id && editData.production_location && editData.planned_quantity > 0 && (
              <>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      backdropFilter: 'blur(10px)',
                      color: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}>
                      <ScienceIcon sx={{ fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 0.5 }}>
                        Batch Feasibility Analysis
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Real-time inventory analysis and material availability
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  {batchAnalysis ? (
                    <Card sx={{ 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(20px)',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                      }
                    }}>
                      <Box sx={{ 
                        p: 3, 
                        background: batchAnalysis.canMakeBatch ? 
                          `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.04)})` : 
                          batchAnalysis.noInventoryAtLocation ?
                          `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)}, ${alpha(theme.palette.warning.main, 0.04)})` :
                          `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`,
                        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            backgroundColor: batchAnalysis.canMakeBatch ? '#4caf50' : 
                                           batchAnalysis.noInventoryAtLocation ? '#ff9800' : '#f44336',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2
                          }}>
                            {batchAnalysis.canMakeBatch ? 
                              <CheckCircleIcon sx={{ fontSize: 28 }} /> : 
                              <WarningIcon sx={{ fontSize: 28 }} />
                            }
                          </Box>
                          <Box>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: '#000000',
                              mb: 0.5
                            }}>
                              {batchAnalysis.canMakeBatch ? 'Batch Can Be Made' : 
                               batchAnalysis.noInventoryAtLocation ? 'No Raw Materials at Location' : 
                               'Insufficient Materials'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)', fontWeight: 500 }}>
                              Real-time inventory analysis complete
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ 
                              textAlign: 'center', 
                              p: 2, 
                              backgroundColor: 'rgba(255,255,255,0.8)', 
                              borderRadius: 2, 
                              border: '1px solid rgba(0,0,0,0.1)',
                              height: 80,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                backgroundColor: 'rgba(255,255,255,1)',
                                border: '1px solid rgba(0,0,0,0.2)'
                              }
                            }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>
                                LOCATION
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                {getLocationLabel(editData.production_location)}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ 
                              textAlign: 'center', 
                              p: 2, 
                              backgroundColor: 'rgba(255,255,255,0.8)', 
                              borderRadius: 2, 
                              border: '1px solid rgba(0,0,0,0.1)',
                              height: 80,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                backgroundColor: 'rgba(255,255,255,1)',
                                border: '1px solid rgba(0,0,0,0.2)'
                              }
                            }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>
                                TARGET BATCH
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                {editData.planned_quantity} {editData.planned_unit_of_measure}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ 
                              textAlign: 'center', 
                              p: 2, 
                              backgroundColor: 'rgba(255,255,255,0.8)', 
                              borderRadius: 2, 
                              border: '1px solid rgba(0,0,0,0.1)',
                              height: 80,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                backgroundColor: 'rgba(255,255,255,1)',
                                border: '1px solid rgba(0,0,0,0.2)'
                              }
                            }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>
                                FORMULATION
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                {selectedFormulation?.name}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ 
                              textAlign: 'center', 
                              p: 2, 
                              backgroundColor: 'rgba(255,255,255,0.8)', 
                              borderRadius: 2, 
                              border: '1px solid rgba(0,0,0,0.1)',
                              height: 80,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                backgroundColor: 'rgba(255,255,255,1)',
                                border: '1px solid rgba(0,0,0,0.2)'
                              }
                            }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>
                                MAX BATCHES
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                                {batchAnalysis.maxBatches === 999 ? '999+' : batchAnalysis.maxBatches}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                      
                      <Stack spacing={3} sx={{ p: 3 }}>



                        {/* Ingredient Analysis Table */}
                        <Card sx={{ 
                          borderRadius: 2,
                          border: '1px solid #2196f3',
                          backgroundColor: 'white',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                          }
                        }}>
                          <Box sx={{ 
                            p: 2, 
                            backgroundColor: '#e3f2fd',
                            borderBottom: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <ScienceIcon sx={{ color: '#2196f3', fontSize: 24, mr: 1.5 }} />
                              <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                                Ingredient-by-Ingredient Analysis
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Box sx={{ p: 3 }}>
                            {/* Check for expiring materials warning */}
                            {(() => {
                              const expiringMaterials = [];
                              if (rawMaterialsInventoryItems && rawMaterialsInventoryItems.length > 0) {
                                rawMaterialsInventoryItems.forEach(inventory => {
                                  if (inventory.expiry_date) {
                                    const daysToExpiry = (new Date(inventory.expiry_date) - new Date()) / (1000 * 60 * 60 * 24);
                                    if (daysToExpiry <= 30 && daysToExpiry >= 0) {
                                      expiringMaterials.push({
                                        name: inventory.raw_material?.name || 'Unknown Material',
                                        lot_number: inventory.lot_number,
                                        batch_number: inventory.batch_number,
                                        expiryDate: inventory.expiry_date,
                                        daysToExpiry: Math.ceil(daysToExpiry)
                                      });
                                    }
                                  }
                                });
                              }
                              
                              if (expiringMaterials.length > 0) {
                                return (
                                  <Alert 
                                    severity="warning" 
                                    sx={{ 
                                      mb: 3,
                                      borderRadius: 2,
                                      border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                                      bgcolor: '#fff3e0',
                                      transition: 'all 0.3s ease',
                                      '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                                      }
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, color: '#000000' }}>
                                      Raw Materials Expiring Soon
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#000000', mb: 1 }}>
                                      {expiringMaterials.length} lot{expiringMaterials.length === 1 ? '' : 's'} will expire within 30 days:
                                    </Typography>
                                    <Box sx={{ ml: 2 }}>
                                      {(showAllExpiringMaterials ? expiringMaterials : expiringMaterials.slice(0, 3)).map((material, index) => (
                                        <Typography key={index} variant="caption" sx={{ display: 'block', color: '#000000' }}>
                                          • {material.name} {material.lot_number ? `(Lot: ${material.lot_number})` : material.batch_number ? `(Batch: ${material.batch_number})` : ''}: Expires in {material.daysToExpiry} day{material.daysToExpiry === 1 ? '' : 's'}
                                        </Typography>
                                      ))}
                                      {expiringMaterials.length > 3 && !showAllExpiringMaterials && (
                                        <Typography 
                                          variant="caption" 
                                          color="primary" 
                                          sx={{ 
                                            cursor: 'pointer', 
                                            textDecoration: 'underline',
                                            fontWeight: 500,
                                            '&:hover': { color: 'primary.dark' }
                                          }}
                                          onClick={() => setShowAllExpiringMaterials(true)}
                                        >
                                          ... and {expiringMaterials.length - 3} more lots (click to show all)
                                        </Typography>
                                      )}
                                      {showAllExpiringMaterials && expiringMaterials.length > 3 && (
                                        <Typography 
                                          variant="caption" 
                                          color="primary" 
                                          sx={{ 
                                            cursor: 'pointer', 
                                            textDecoration: 'underline',
                                            fontWeight: 500,
                                            '&:hover': { color: 'primary.dark' }
                                          }}
                                          onClick={() => setShowAllExpiringMaterials(false)}
                                        >
                                          (click to show less)
                                        </Typography>
                                      )}
                                    </Box>
                                  </Alert>
                                );
                              }
                              return null;
                            })()}
                            
                            {batchAnalysis.ingredients.length > 0 ? (
                              <Grid container spacing={3}>
                                {(showAllEditDialogIngredients ? batchAnalysis.ingredients : batchAnalysis.ingredients.slice(0, 6)).map((ingredient, index) => {
                                  const isAvailable = ingredient.hasInventory && ingredient.canMake > 0;
                                  const hasInventory = ingredient.hasInventory;
                                  
                                  return (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                      <Card sx={{ 
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        height: 200,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'all 0.3s ease',
                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                                        boxShadow: `0 2px 8px ${alpha(theme.palette.grey[500], 0.08)}`,
                                        '&:hover': {
                                          transform: 'translateY(-2px)',
                                          boxShadow: `0 4px 16px ${alpha(theme.palette.grey[500], 0.12)}`,
                                          borderColor: isAvailable ? theme.palette.success.main : hasInventory ? theme.palette.error.main : theme.palette.warning.main
                                        }
                                      }}>
                                        {/* Status Header */}
                                        <Box sx={{ 
                                          backgroundColor: isAvailable 
                                            ? '#e8f5e8'
                                            : hasInventory 
                                            ? '#ffebee'
                                            : '#fff3e0',
                                          color: isAvailable 
                                            ? theme.palette.success.main
                                            : hasInventory 
                                            ? theme.palette.error.main
                                            : theme.palette.warning.main,
                                          p: 1.5,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderBottom: `1px solid ${isAvailable 
                                            ? alpha(theme.palette.success.main, 0.2)
                                            : hasInventory 
                                            ? alpha(theme.palette.error.main, 0.2)
                                            : alpha(theme.palette.warning.main, 0.2)}`
                                        }}>
                                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem', textAlign: 'center' }}>
                                            {isAvailable ? 'AVAILABLE' : hasInventory ? 'INSUFFICIENT' : 'NO STOCK'}
                                          </Typography>
                                        </Box>
                                        
                                        {/* Content */}
                                        <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                          <Box>
                                            <Typography variant="h6" sx={{ 
                                              fontWeight: 500, 
                                              fontSize: '1rem', 
                                              mb: -1, 
                                              color: '#000000',
                                              lineHeight: 1.3,
                                              display: '-webkit-box',
                                              WebkitLineClamp: 2,
                                              WebkitBoxOrient: 'vertical',
                                              overflow: 'hidden',
                                              minHeight: '2.6em'
                                            }}>
                                              {ingredient.name}
                                            </Typography>
                                          </Box>
                                          
                                          <Box>
                                            <Box sx={{ 
                                              display: 'flex', 
                                              justifyContent: 'center', 
                                              mt: 0,
                                              mb: 0,
                                              mx: 1,
                                              p: 1.5,
                                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                              borderRadius: 2,
                                              border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                                              boxShadow: `0 1px 4px ${alpha(theme.palette.grey[500], 0.1)}`
                                            }}>
                                              <Box sx={{ textAlign: 'center', flex: 1 }}>
                                                <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, display: 'block' }}>
                                                  NEED
                                                </Typography>
                                                                                                 <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                                   {ingredient.required.toFixed(3)}
                                                 </Typography>
                                                 <Typography variant="caption" sx={{ color: '#666' }}>
                                                   {ingredient.requiredUnit}
                                                 </Typography>
                                               </Box>
                                               <Box sx={{ textAlign: 'center', flex: 1, borderLeft: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`, pl: 2 }}>
                                                 <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, display: 'block' }}>
                                                   HAVE
                                                 </Typography>
                                                 <Typography variant="body2" sx={{ 
                                                   fontWeight: 500, 
                                                   color: isAvailable ? '#4caf50' : hasInventory ? '#f44336' : '#ff9800'
                                                 }}>
                                                   {ingredient.available.toFixed(3)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#666' }}>
                                                  {ingredient.requiredUnit}
                                                </Typography>
                                              </Box>
                                            </Box>
                                            
                                            {!hasInventory && (
                                              <Typography variant="body2" sx={{ 
                                                textAlign: 'center', 
                                                color: '#ff9800', 
                                                fontWeight: 500,
                                                fontStyle: 'italic'
                                              }}>
                                                Order this material for production
                                              </Typography>
                                            )}
                                          </Box>
                                        </Box>
                                      </Card>
                                    </Grid>
                                  );
                                })}
                                
                                {/* Show more/less for ingredients in edit dialog */}
                                {batchAnalysis.ingredients.length > 6 && !showAllEditDialogIngredients && (
                                  <Grid item xs={12}>
                                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                                      <Typography 
                                        variant="body2" 
                                        color="primary" 
                                        sx={{ 
                                          cursor: 'pointer', 
                                          textDecoration: 'underline',
                                          fontWeight: 500,
                                          '&:hover': { color: 'primary.dark' }
                                        }}
                                        onClick={() => setShowAllEditDialogIngredients(true)}
                                      >
                                        ... and {batchAnalysis.ingredients.length - 6} more ingredients (click to show all)
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                                {showAllEditDialogIngredients && batchAnalysis.ingredients.length > 6 && (
                                  <Grid item xs={12}>
                                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                                      <Typography 
                                        variant="body2" 
                                        color="primary" 
                                        sx={{ 
                                          cursor: 'pointer', 
                                          textDecoration: 'underline',
                                          fontWeight: 500,
                                          '&:hover': { color: 'primary.dark' }
                                        }}
                                        onClick={() => setShowAllEditDialogIngredients(false)}
                                      >
                                        (click to show less)
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                No formulation ingredients found.
                              </Typography>
                            )}
                          </Box>
                        </Card>

                        {/* Recommendation */}
                        <Card sx={{ 
                          borderRadius: 2,
                          border: '1px solid #4caf50',
                          backgroundColor: 'white',
                          boxShadow: '0 4px 16px rgba(76, 175, 80, 0.12)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                          }
                        }}>
                          <Box sx={{ 
                            p: 3, 
                            backgroundColor: '#e8f5e8',
                            borderBottom: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <AttachMoneyIcon sx={{ color: '#4caf50', fontSize: 28, mr: 1.5 }} />
                              <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                                Recommendation
                              </Typography>
                            </Box>
                            <Typography variant="body1" sx={{ color: '#000000', fontWeight: 500, lineHeight: 1.6 }}>
                              {batchAnalysis.canMakeBatch ? (
                                batchAnalysis.maxBatches > 1 ? 
                                  `You have enough materials to make ${batchAnalysis.maxBatches === 999 ? 'many' : batchAnalysis.maxBatches} batches of this size. Consider bulk production for efficiency.` :
                                  'You have just enough materials for this batch. Consider ordering more materials for future production runs.'
                              ) : batchAnalysis.noInventoryAtLocation ? (
                                `No raw materials found at ${getLocationLabel(editData.production_location)}. Transfer materials to this location or choose a different production facility with existing inventory.`
                              ) : (
                                batchAnalysis.shortages.length === 1 ? 
                                  `Order ${batchAnalysis.shortages[0].shortage.toFixed(2)} ${batchAnalysis.shortages[0].unit} of ${batchAnalysis.shortages[0].name} to complete this batch.` :
                                  `Order the missing materials listed above, or reduce the batch size to ${Math.floor(editData.planned_quantity * (batchAnalysis.maxBatches / 1))} ${editData.planned_unit_of_measure} to make with current inventory.`
                              )}
                            </Typography>
                          </Box>
                        </Card>
                      </Stack>
                    </Card>
                  ) : (
                    <Box sx={{ 
                      p: 3, 
                      backgroundColor: alpha('#ff9800', 0.1), 
                      borderRadius: 2,
                      border: `1px solid ${alpha('#ff9800', 0.3)}`,
                      textAlign: 'center'
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        {!editData.formulation_id ? 'Select a formulation to analyze batch feasibility' :
                         !editData.production_location ? 'Select a production location to check material availability' :
                         'Loading batch analysis...'}
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </>
            )}
                </Grid>
              </StepContent>
            </Step>
          </Stepper>
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          justifyContent: 'space-between'
        }}>
          <Button 
            onClick={handleCancel} 
            startIcon={<CancelIcon />}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Cancel
          </Button>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            {activeStep > 0 && (
              <Button 
                onClick={handleBack}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                  }
                }}
              >
                Back
              </Button>
            )}
            
            {activeStep < steps.length - 1 ? (
              <Button 
                onClick={handleNext}
                variant="contained"
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  color: 'white',
                  fontWeight: 500,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                  }
                }}
              >
                Next
              </Button>
            ) : (
              <Button 
                onClick={handleSave}
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={saving ||
                  !editData.batch_number ||
                  !editData.status ||
                  !editData.product_id || 
                  !editData.formulation_id || 
                  !editData.production_location || 
                  !editData.manufacture_date || 
                  !editData.expiration_date || 
                  !editData.planned_start_datetime || 
                  !editData.planned_completion_datetime || 
                  !editData.priority || 
                  !editData.production_line || 
                  !editData.shift_supervisor ||
                  !editData.variant_distribution || 
                  editData.variant_distribution.length === 0 ||
                  !editData.variant_distribution.some(v => v.quantity > 0)
                }
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  color: 'white',
                  fontWeight: 500,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                  },
                  '&:disabled': {
                    background: theme.palette.grey[400],
                    color: theme.palette.grey[100]
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save Batch'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Product Selection Dialog */}
      <Dialog
        open={productSelectionDialogOpen}
        onClose={handleCloseProductSelection}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 4,
            height: '100vh',
            margin: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          fontSize: '1.25rem', 
          fontWeight: 500,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
            Select Product
          </Typography>
          <IconButton onClick={handleCloseProductSelection} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search products..."
              value={productSearchTerm}
              onChange={(e) => setProductSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <InventoryIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            {!dialogSelectedProduct ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredProducts().map((product) => (
                    <TableRow 
                      key={product.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'grey.50',
                          transform: 'translateX(4px)',
                          boxShadow: 'inset 4px 0 0 black'
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                          py: 2,
                          pl: 3,
                          pr: 3
                        }
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {product.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            SKU: {product.sku || 'No SKU'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={async () => {
                            setDialogSelectedProduct(product);
                            await fetchProductVariants(product.id);
                            // Auto-select product in editData for immediate feedback
                            setEditData(prev => ({
                              ...prev,
                              product_id: product.id,
                              product_variant_id: null,
                              product_variant: ''
                            }));
                          }}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            px: 2,
                            py: 1,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            bgcolor: dialogSelectedProduct?.id === product.id ? 'black' : 'transparent',
                            color: dialogSelectedProduct?.id === product.id ? 'white' : 'inherit',
                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                            '&:hover': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              bgcolor: dialogSelectedProduct?.id === product.id ? 'black' : 'transparent',
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                            }
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {getFilteredProducts().length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {productSearchTerm ? 'No products found matching your search.' : 'No products available.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            ) : (
              <Box sx={{ p: 3 }}>
                {/* Selected Product Display */}
                <Box sx={{ 
                  mb: 3, 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Selected Product:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {dialogSelectedProduct.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    SKU: {dialogSelectedProduct.sku || 'No SKU'}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      setDialogSelectedProduct(null);
                      setEditData(prev => ({
                        ...prev,
                        product_id: '',
                        product_variant_id: null,
                        product_variant: ''
                      }));
                    }}
                    sx={{ ml: 2, textTransform: 'none', fontSize: '0.8rem' }}
                  >
                    Change Product
                  </Button>
                </Box>

                {/* Variant Selection */}
                {productVariants && productVariants.length > 0 ? (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
                      Select Variant:
                    </Typography>
                    {productVariants.length > 0 ? (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Variant</TableCell>
                            <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {productVariants.map((variant) => (
                            <TableRow 
                              key={variant.id}
                              hover
                              sx={{
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: 'grey.50',
                                  transform: 'translateX(4px)',
                                  boxShadow: 'inset 4px 0 0 black'
                                },
                                '& .MuiTableCell-root': {
                                  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                  py: 2,
                                  pl: 3,
                                  pr: 3
                                }
                              }}
                            >
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                    {variant.name || `${variant.volume_weight}${variant.volume_unit}`}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    SKU: {variant.sku || 'No SKU'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={async () => {
                                    await handleVariantChange(variant.id);
                                    handleCloseProductSelection();
                                  }}
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '0.8rem',
                                    px: 2,
                                    py: 1,
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                    bgcolor: editData.product_variant_id === variant.id ? 'black' : 'transparent',
                                    color: editData.product_variant_id === variant.id ? 'white' : 'inherit',
                                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                    '&:hover': {
                                      border: `1px solid ${theme.palette.primary.main}`,
                                      bgcolor: editData.product_variant_id === variant.id ? 'black' : 'transparent',
                                      transform: 'translateY(-1px)',
                                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                                    }
                                  }}
                                >
                                  Select
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No variants available for this product.
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                  }}>
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                      ✓ This product has no variants. Ready to proceed.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          bgcolor: alpha(theme.palette.grey[50], 0.5),
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Button
            onClick={() => window.open(`/company/${userCompanyId}/products`, '_blank')}
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{ 
              borderRadius: 2, 
              px: 3, 
              py: 1, 
              fontSize: '0.9rem', 
              fontWeight: 500,
              textTransform: 'none',
              border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
              color: 'success.main',
              '&:hover': {
                border: `1px solid ${theme.palette.success.main}`,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                transform: 'translateY(-1px)'
              }
            }}
          >
            Add Product
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={handleCloseProductSelection}
              variant="outlined"
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  border: `1px solid ${theme.palette.grey[600]}`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmProductSelection}
              variant="contained"
              disabled={!dialogSelectedProduct || (productVariants?.length > 0 && productVariants.length > 0)}
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                },
                '&:disabled': {
                  background: alpha(theme.palette.grey[400], 0.5),
                  color: alpha(theme.palette.grey[600], 0.7)
                }
              }}
            >
              {productVariants?.length > 0 ? 'Select Variant Above' : 'Confirm Selection'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Formulation Selection Dialog */}
      <Dialog
        open={formulationSelectionDialogOpen}
        onClose={handleCloseFormulationSelection}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
            height: '100vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ScienceIcon sx={{ mr: 2 }} />
            Select Formulation
          </Box>
          <IconButton 
            onClick={handleCloseFormulationSelection} 
            size="small"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                transform: 'scale(1.1)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search formulations by name, version, or description..."
              value={formulationSearchTerm}
              onChange={(e) => setFormulationSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ScienceIcon color="action" />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  fontSize: '0.9rem',
                  '& fieldset': { border: 'none' },
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
            />
          </Box>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                  }}>
                    Formulation
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                  }}>
                    Base Size
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    width: 100
                  }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredFormulations().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        {formulationSearchTerm ? 'No formulations found matching your search.' : 'No approved formulations available.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  getFilteredFormulations().map((formulation) => (
                    <TableRow
                      key={formulation.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05)
                        }
                      }}
                      onClick={() => handleSelectFormulation(formulation)}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {formulation.name} {formulation.version && `(v${formulation.version})`}
                          </Typography>
                          {formulation.description && (
                            <Typography variant="caption" color="text.secondary">
                              {formulation.description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {formulation.target_batch_size || 'Not set'} {formulation.unit_of_measure || formulation.target_batch_unit || 'gal'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectFormulation(formulation);
                          }}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            px: 2,
                            py: 0.5,
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                            }
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
          <Button 
            onClick={handleCloseFormulationSelection}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                transform: 'translateY(-1px)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.15)}`
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => window.open(`/company/${userCompanyId}/formulations`, '_blank')}
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                transform: 'translateY(-1px)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
              }
            }}
          >
            Add Formulation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location Selection Dialog */}
      <Dialog
        open={locationSelectionDialogOpen}
        onClose={handleCloseLocationSelection}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
            height: '100vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 2 }} />
            Select Production Location
          </Box>
          <IconButton 
            onClick={handleCloseLocationSelection} 
            size="small"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                transform: 'scale(1.1)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search locations by name, facility type, city, or state..."
              value={locationSearchTerm}
              onChange={(e) => setLocationSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon color="action" />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  fontSize: '0.9rem',
                  '& fieldset': { border: 'none' },
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
            />
          </Box>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                  }}>
                    Location
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    width: 100
                  }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredLocations().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        {locationSearchTerm ? 'No locations found matching your search.' : 'No production locations available.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  getFilteredLocations().map((location) => (
                    <TableRow
                      key={location.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05)
                        }
                      }}
                      onClick={() => handleSelectLocation(location)}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          {location.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectLocation(location);
                          }}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            px: 2,
                            py: 0.5,
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                            }
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
          <Button 
            onClick={handleCloseLocationSelection}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                transform: 'translateY(-1px)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.15)}`
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => window.open(`/company/${userCompanyId}/locations`, '_blank')}
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                transform: 'translateY(-1px)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
              }
            }}
          >
            Add Location
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unit Configuration Selection Dialog */}
      <Dialog
        open={unitConfigSelectionDialogOpen}
        onClose={handleCloseUnitConfigSelection}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            height: '100vh',
            margin: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          fontSize: '1.25rem', 
          fontWeight: 500,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
            Select Unit Configuration
          </Typography>
          <IconButton onClick={handleCloseUnitConfigSelection} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search unit configurations..."
              value={unitConfigSearchTerm}
              onChange={(e) => setUnitConfigSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SettingsIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            {getFilteredUnitConfigurations().length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {unitConfigSearchTerm ? 'No unit configurations found matching your search.' : 'No unit configurations available.'}
                </Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Configuration</TableCell>
                    <TableCell sx={{ fontWeight: 500, py: 2 }}>Structure</TableCell>
                    <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Details</TableCell>
                    <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredUnitConfigurations().map((config) => {
                    const isSelected = selectedUnitConfig?.id === config.id;
                    const getTotalUnitsPerPallet = (config) => {
                      if (config.enable_pallets && config.enable_cases) {
                        return config.units_per_case * config.cases_per_pallet;
                      }
                      return null;
                    };

                    return (
                      <TableRow 
                        key={config.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                          '&:hover': {
                            bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.1) : 'grey.50',
                            transform: 'translateX(4px)',
                            boxShadow: 'inset 4px 0 0 black'
                          },
                          '& .MuiTableCell-root': {
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                            py: 2,
                            pl: 3,
                            pr: 3
                          }
                        }}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                              {config.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                              {config.enable_cases && (
                                <Chip 
                                  label="Cases" 
                                  size="small" 
                                  sx={{
                                    bgcolor: alpha(theme.palette.info.main, 0.1),
                                    color: 'info.main',
                                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                    fontWeight: 500,
                                    fontSize: '0.7rem',
                                    height: 20
                                  }}
                                />
                              )}
                              {config.enable_pallets && (
                                <Chip 
                                  label="Pallets" 
                                  size="small" 
                                  sx={{
                                    bgcolor: alpha(theme.palette.success.main, 0.1),
                                    color: 'success.main',
                                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                    fontWeight: 500,
                                    fontSize: '0.7rem',
                                    height: 20
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                            {config.enable_cases && !config.enable_pallets && `${config.base_unit_name} → ${config.case_unit_name}`}
                            {config.enable_cases && config.enable_pallets && `${config.base_unit_name} → ${config.case_unit_name} → ${config.pallet_unit_name}`}
                            {!config.enable_cases && !config.enable_pallets && `${config.base_unit_name} only`}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                              Units/Case: {config.enable_cases ? config.units_per_case : 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                              Cases/Pallet: {config.enable_pallets ? config.cases_per_pallet : 'N/A'}
                            </Typography>
                            {getTotalUnitsPerPallet(config) && (
                              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500, display: 'block', mt: 0.5 }}>
                                Total: {getTotalUnitsPerPallet(config).toLocaleString()}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleSelectUnitConfig(config)}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 500,
                              fontSize: '0.8rem',
                              px: 2,
                              py: 1,
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                              bgcolor: isSelected ? 'black' : 'transparent',
                              color: isSelected ? 'white' : 'inherit',
                              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                              '&:hover': {
                                border: `1px solid ${theme.palette.primary.main}`,
                                bgcolor: isSelected ? 'black' : 'transparent',
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                              }
                            }}
                          >
                            {isSelected ? 'Selected' : 'Select'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          bgcolor: alpha(theme.palette.grey[50], 0.5),
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Button
            onClick={() => {
              handleCloseUnitConfigSelection();
              setUnitConfigDialogOpen(true);
            }}
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{ 
              borderRadius: 2, 
              px: 3, 
              py: 1, 
              fontSize: '0.9rem', 
              fontWeight: 500,
              textTransform: 'none',
              border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
              color: 'success.main',
              '&:hover': {
                border: `1px solid ${theme.palette.success.main}`,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                transform: 'translateY(-1px)'
              }
            }}
          >
            Add Configuration
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={handleCloseUnitConfigSelection}
              variant="outlined"
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  border: `1px solid ${theme.palette.grey[600]}`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Cancel
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Unit Configuration Creation/Edit Dialog */}
      <Dialog
        open={unitConfigDialogOpen}
        onClose={handleCloseUnitConfigDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: 3
        }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <SettingsIcon sx={{ fontSize: 28, color: 'primary.main' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            {editingUnitConfig ? 'Edit Configuration' : 'Add Configuration'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={5}>
            <Grid item xs={12}>
              <Box sx={{ 
                p: 3, 
                borderRadius: 2, 
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                mb: 3,
                mt: 4
              }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 500, 
                  mb: 3,
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}>
                  <SettingsIcon sx={{ fontSize: 28 }} />
                  Basic Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="medium"
                      label="Configuration Name"
                      value={unitConfigFormData.name}
                      onChange={(e) => setUnitConfigFormData({...unitConfigFormData, name: e.target.value})}
                      placeholder="e.g., Standard Bottles, Large Cases, Individual Units"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          '& fieldset': {
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            borderWidth: 1
                          },
                          '&:hover fieldset': {
                            borderColor: theme.palette.primary.main
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main
                          }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="medium"
                      label="Base Unit Name"
                      value={unitConfigFormData.base_unit_name}
                      onChange={(e) => setUnitConfigFormData({...unitConfigFormData, base_unit_name: e.target.value})}
                      placeholder="e.g., units, bottles, pieces"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          '& fieldset': {
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            borderWidth: 1
                          },
                          '&:hover fieldset': {
                            borderColor: theme.palette.primary.main
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                p: 3, 
                borderRadius: 2, 
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                mb: 3
              }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 500, 
                  mb: 3,
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}>
                  <InventoryIcon sx={{ fontSize: 28 }} />
                  Packaging Structure
                </Typography>
                
                {/* Cases Configuration */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={unitConfigFormData.enable_cases}
                          onChange={(e) => setUnitConfigFormData({
                            ...unitConfigFormData, 
                            enable_cases: e.target.checked,
                            enable_pallets: e.target.checked ? unitConfigFormData.enable_pallets : false
                          })}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                          Enable Cases
                        </Typography>
                      }
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                      Group individual units into cases for easier handling
                    </Typography>
                  </Grid>
                  
                  {unitConfigFormData.enable_cases && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Case Unit Name"
                          value={unitConfigFormData.case_unit_name}
                          onChange={(e) => setUnitConfigFormData({...unitConfigFormData, case_unit_name: e.target.value})}
                          placeholder="e.g., cases, boxes, packs"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(10px)',
                              '& fieldset': {
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                                borderWidth: 1
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.primary.main
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Units per Case"
                          type="number"
                          value={unitConfigFormData.units_per_case}
                          onChange={(e) => setUnitConfigFormData({...unitConfigFormData, units_per_case: e.target.value})}
                          InputProps={{
                            inputProps: { min: 1 }
                          }}
                          helperText="How many individual units make up one case"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(10px)',
                              '& fieldset': {
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                                borderWidth: 1
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.primary.main
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main
                              }
                            }
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>

                {/* Pallets Configuration */}
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={unitConfigFormData.enable_pallets}
                          onChange={(e) => setUnitConfigFormData({...unitConfigFormData, enable_pallets: e.target.checked})}
                          color="primary"
                          disabled={!unitConfigFormData.enable_cases}
                        />
                      }
                      label={
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                          Enable Pallets
                        </Typography>
                      }
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                      Group cases into pallets for bulk storage {!unitConfigFormData.enable_cases && '(requires cases to be enabled)'}
                    </Typography>
                  </Grid>
                  
                  {unitConfigFormData.enable_pallets && unitConfigFormData.enable_cases && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Pallet Unit Name"
                          value={unitConfigFormData.pallet_unit_name}
                          onChange={(e) => setUnitConfigFormData({...unitConfigFormData, pallet_unit_name: e.target.value})}
                          placeholder="e.g., pallets, skids"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(10px)',
                              '& fieldset': {
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                                borderWidth: 1
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.primary.main
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Cases per Pallet"
                          type="number"
                          value={unitConfigFormData.cases_per_pallet}
                          onChange={(e) => setUnitConfigFormData({...unitConfigFormData, cases_per_pallet: e.target.value})}
                          InputProps={{
                            inputProps: { min: 1 }
                          }}
                          helperText="How many cases fit on one pallet"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(10px)',
                              '& fieldset': {
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                                borderWidth: 1
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.primary.main
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main
                              }
                            }
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>

                {/* Quick Presets */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                    Quick Presets
                  </Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <Button 
                      variant="outlined" 
                      size="medium"
                      onClick={() => setUnitConfigFormData({...unitConfigFormData, enable_cases: false, enable_pallets: false})}
                      sx={{ borderRadius: 2, fontSize: '0.9rem', px: 3 }}
                    >
                      Units Only
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="medium"
                      onClick={() => setUnitConfigFormData({...unitConfigFormData, enable_cases: true, enable_pallets: false, units_per_case: 12})}
                      sx={{ borderRadius: 2, fontSize: '0.9rem', px: 3 }}
                    >
                      Cases (12 units)
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="medium"
                      onClick={() => setUnitConfigFormData({...unitConfigFormData, enable_cases: true, enable_pallets: true, units_per_case: 12, cases_per_pallet: 42})}
                      sx={{ borderRadius: 2, fontSize: '0.9rem', px: 3 }}
                    >
                      Full (12×42)
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="medium"
                      onClick={() => setUnitConfigFormData({...unitConfigFormData, enable_cases: true, enable_pallets: true, units_per_case: 24, cases_per_pallet: 48})}
                      sx={{ borderRadius: 2, fontSize: '0.9rem', px: 3 }}
                    >
                      Large (24×48)
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={handleCloseUnitConfigDialog} 
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUnitConfigSubmit}
            disabled={savingUnitConfig || !unitConfigFormData.name}
            startIcon={savingUnitConfig ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                transform: 'translateY(-2px)'
              },
              '&:disabled': {
                background: theme.palette.grey[400],
                color: theme.palette.grey[100]
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {savingUnitConfig ? 'Saving...' : (editingUnitConfig ? 'Update Configuration' : 'Create Configuration')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BatchOverview; 